import { formatMoney } from '../shared/format/money.ts';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  FileSpreadsheet,
  FileText,
  Image,
  Upload,
  CheckCircle2,
  Loader2,
  Check,
  AlertTriangle,
  AlertCircle,
  Download,
  Plus,
  Minus,
  X,
  Play,
  Info,
  RotateCcw,
  MessageSquare,
  Sparkles,
  Layers,
  XCircle,
  Flag,
  Users,
  ClipboardList,
  BarChart3,
  FileWarning,
  ChevronDown,
  ChevronUp,
  Calendar
} from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { isControlUuid, resolveControlUuid } from '../features/controls/functional.ts';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import { MODULE_KEYS } from '../constants/moduleAccess.ts';
import { useRequireModuleAccess } from '../hooks/useRequireModuleAccess.ts';
import ModuleAccessDeniedDialog from './shared/ModuleAccessDeniedDialog.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import EvidenceViewer from './EvidenceViewer';

// Helper function to format duration in human-readable format
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return 'N/A';
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
};

// Collapsible section showing deduplicated annotation images per step
/* ── Zoomable image (inline evidence viewer) ─────────────────────────────────── */
const INLINE_ZOOM_MIN = 0.25;
const INLINE_ZOOM_MAX = 8;

function ZoomableImage({ src, alt }) {
  const [view, _setView] = useState({ zoom: 1, panX: 0, panY: 0 });
  const viewRef = useRef(view);
  const setView = useCallback((updater) => {
    _setView(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      viewRef.current = next;
      return next;
    });
  }, []);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, initPanX: 0, initPanY: 0 });
  const containerRef = useRef(null);

  const resetView = useCallback(() => setView({ zoom: 1, panX: 0, panY: 0 }), [setView]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 0.88;
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      setView(v => {
        const nextZoom = Math.min(Math.max(v.zoom * factor, INLINE_ZOOM_MIN), INLINE_ZOOM_MAX);
        return { zoom: nextZoom, panX: cx - ((cx - v.panX) * nextZoom / v.zoom), panY: cy - ((cy - v.panY) * nextZoom / v.zoom) };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setView]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      setView(v => ({ ...v, panX: dragRef.current.initPanX + (e.clientX - dragRef.current.startX), panY: dragRef.current.initPanY + (e.clientY - dragRef.current.startY) }));
    };
    const onUp = () => { if (!dragRef.current.active) return; dragRef.current.active = false; setIsDragging(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [setView]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, initPanX: viewRef.current.panX, initPanY: viewRef.current.panY };
    setIsDragging(true);
  }, []);

  const zoomPct = Math.round(view.zoom * 100);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-100 border-b border-gray-200">
        <button onClick={() => setView(v => ({ ...v, zoom: Math.max(v.zoom / 1.25, INLINE_ZOOM_MIN) }))} disabled={view.zoom <= INLINE_ZOOM_MIN}
          className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 rounded transition-colors" title="Zoom out">
          <Minus className="h-3 w-3" />
        </button>
        <button onClick={resetView} className="px-1.5 py-0.5 text-xs font-mono text-gray-600 hover:text-gray-900 min-w-[3rem] text-center rounded hover:bg-gray-200 transition-colors" title="Reset zoom">
          {zoomPct}%
        </button>
        <button onClick={() => setView(v => ({ ...v, zoom: Math.min(v.zoom * 1.25, INLINE_ZOOM_MAX) }))} disabled={view.zoom >= INLINE_ZOOM_MAX}
          className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30 rounded transition-colors" title="Zoom in">
          <Plus className="h-3 w-3" />
        </button>
        <span className="text-xs text-gray-400 ml-2">Scroll · Drag · Double-click to reset</span>
      </div>
      <div ref={containerRef} className="relative overflow-hidden select-none bg-gray-50"
        style={{ height: '500px', cursor: isDragging ? 'grabbing' : view.zoom > 1 ? 'grab' : 'default' }}
        onMouseDown={handleMouseDown}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          transform: `translate(${view.panX}px, ${view.panY}px) scale(${view.zoom})`,
          transformOrigin: 'center center', transition: isDragging ? 'none' : 'transform 0.12s ease-out' }}
          onDoubleClick={resetView}>
          <img src={src} alt={alt} draggable={false}
            className="object-contain rounded border border-gray-200"
            style={{ maxWidth: 'calc(100% - 16px)', maxHeight: 'calc(100% - 16px)' }} />
        </div>
      </div>
    </div>
  );
}

// One annotation per unique evidence file+sheet/page, with auditor metadata overlay
function EvidenceAnnotationsSection({ stepResult, control, evidenceFiles }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [annotations, setAnnotations] = useState({}); // key -> { annotatedUrl, originalUrl, loading, error }
  const [viewMode, setViewMode] = useState({}); // key -> 'annotated' | 'original'
  const fetchedRef = useRef(new Set());

  // Get auditor name from localStorage, date from today
  const auditorName = localStorage.getItem('username') || 'Auditor';
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit'
  });

  // Build deduplicated evidence entries: one per unique file+sheet/page
  const uniqueEvidence = React.useMemo(() => {
    if (!stepResult) return [];
    const seen = new Map(); // key -> citation

    // Resolve file ID from filename using evidence store files or data_source_config.
    // Citations persisted during test execution sometimes keep the chunk-prefix
    // form (e.g. "4_PR 286978.pdf") when backend resolve_citation could not
    // match against file_id_map. Strip the numeric prefix so we still hydrate.
    const stripChunkPrefix = (name) => (name || '').replace(/^\d+_/, '');
    const resolveFileId = (cite) => {
      if (cite.source_file_id) return cite;
      if (!cite.source_file) return cite;
      const raw = cite.source_file;
      const stripped = stripChunkPrefix(raw);
      const candidates = stripped !== raw ? [raw, stripped] : [raw];
      // Try evidence store files first (primary source)
      if (evidenceFiles?.length > 0) {
        for (const cand of candidates) {
          const match = evidenceFiles.find(f => f.name === cand);
          if (match) {
            return { ...cite, source_file_id: match.controlUploadId || match.evidenceId, source_file: match.name };
          }
        }
      }
      // Fallback to data_source_config.files
      if (control?.data_source_config?.files) {
        for (const cand of candidates) {
          const match = control.data_source_config.files.find(f => f.name === cand);
          if (match?.id) {
            return { ...cite, source_file_id: match.id, source_file: match.name };
          }
        }
      }
      return cite;
    };

    // Collect from sample_results citations
    for (const sample of (stepResult.sample_results || [])) {
      const cite = sample.citation;
      if (!cite?.source_file_id && !cite?.source_file) continue;
      const resolvedCite = resolveFileId(cite);
      if (!resolvedCite.source_file_id) continue;
      const key = `${resolvedCite.source_file_id}|${resolvedCite.sheet_name || ''}|${resolvedCite.page_number || ''}`;
      if (!seen.has(key)) {
        seen.set(key, resolvedCite);
      }
    }

    // Also collect from exception citations
    for (const exc of (stepResult.exceptions || [])) {
      const cite = exc.citation;
      if (!cite?.source_file_id && !cite?.source_file) continue;
      const resolvedCite = resolveFileId(cite);
      if (!resolvedCite.source_file_id) continue;
      const key = `${resolvedCite.source_file_id}|${resolvedCite.sheet_name || ''}|${resolvedCite.page_number || ''}`;
      if (!seen.has(key)) {
        seen.set(key, resolvedCite);
      }
    }

    return Array.from(seen.entries()).map(([key, cite]) => ({ key, citation: cite }));
  }, [stepResult, control, evidenceFiles]);

  // Fetch annotations when expanded
  useEffect(() => {
    if (!isExpanded || uniqueEvidence.length === 0) return;

    for (const { key, citation } of uniqueEvidence) {
      if (fetchedRef.current.has(key)) continue;
      fetchedRef.current.add(key);

      setAnnotations(prev => ({ ...prev, [key]: { annotatedUrl: null, originalUrl: null, loading: true, error: null } }));
      setViewMode(prev => ({ ...prev, [key]: 'annotated' }));

      const requestBody = {
        file_id: citation.source_file_id,
        filename: citation.source_file || '',
        citation: {
          source_file: citation.source_file || '',
          source_file_id: citation.source_file_id,
          sheet_name: citation.sheet_name || null,
          page_number: citation.page_number || null,
          row_numbers: citation.row_numbers || null,
          cell_ref: citation.cell_ref || null,
          excerpt: citation.excerpt || '',
        },
        findings_text: stepResult.findings_summary || '',
      };

      // Fetch annotated first, then original (sequential to avoid browser connection limits)
      const fetchImage = async (url, timeout) => {
        try {
          const resp = await axiosInstance.post(url, requestBody, {
            responseType: 'arraybuffer', timeout,
          });
          if (resp.status === 200 && resp.data?.byteLength > 0) {
            return URL.createObjectURL(new Blob([resp.data], { type: 'image/png' }));
          }
          return null;
        } catch { return null; }
      };

      (async () => {
        const annotatedUrl = await fetchImage('/api/evidence/annotate', 90000);
        const originalUrl = await fetchImage('/api/evidence/screenshot', 60000);
        setAnnotations(prev => ({
          ...prev,
          [key]: {
            annotatedUrl,
            originalUrl,
            loading: false,
            error: !annotatedUrl && !originalUrl ? 'No evidence available' : null,
          }
        }));
      })();
    }
  }, [isExpanded, uniqueEvidence, stepResult]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(annotations).forEach(a => {
        if (a.annotatedUrl) URL.revokeObjectURL(a.annotatedUrl);
        if (a.originalUrl) URL.revokeObjectURL(a.originalUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (uniqueEvidence.length === 0) return null;

  return (
    <div className="border border-purple-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-purple-50 hover:bg-purple-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-purple-600" />
          <span className="text-sm font-medium text-purple-900">
            Evidence Annotations ({uniqueEvidence.length})
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-purple-600" />
        ) : (
          <ChevronDown className="h-4 w-4 text-purple-600" />
        )}
      </button>
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {uniqueEvidence.map(({ key, citation }) => {
            const ann = annotations[key];
            return (
              <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate">{citation.source_file || 'Evidence'}</span>
                    <span className="text-xs text-gray-500">
                      {citation.sheet_name && `Sheet: ${citation.sheet_name}`}
                      {citation.page_number && `Page ${citation.page_number}`}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                    Tested by: {auditorName} | {currentDate}
                  </span>
                </div>
                {/* Annotated / Original toggle */}
                {ann && !ann.loading && (ann.annotatedUrl || ann.originalUrl) && (
                  <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                    <button
                      onClick={() => setViewMode(prev => ({ ...prev, [key]: 'annotated' }))}
                      className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${(viewMode[key] || 'annotated') === 'annotated'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                    >
                      Annotated
                    </button>
                    <button
                      onClick={() => setViewMode(prev => ({ ...prev, [key]: 'original' }))}
                      className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${(viewMode[key] || 'annotated') === 'original'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                    >
                      Original
                    </button>
                  </div>
                )}
                <div className="bg-gray-50">
                  {ann?.loading && (
                    <div className="flex items-center justify-center gap-2 min-h-[120px]">
                      <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
                      <span className="text-sm text-gray-500">Loading evidence...</span>
                    </div>
                  )}
                  {ann && !ann.loading && (() => {
                    const mode = viewMode[key] || 'annotated';
                    const url = mode === 'annotated' ? (ann.annotatedUrl || ann.originalUrl) : (ann.originalUrl || ann.annotatedUrl);
                    return url ? <ZoomableImage src={url} alt={`${mode} evidence`} /> : null;
                  })()}
                  {ann?.error && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <AlertCircle className="h-4 w-4" />
                      {ann.error}
                    </div>
                  )}
                  {!ann && (
                    <span className="text-sm text-gray-400">Click to expand and load evidence</span>
                  )}
                </div>
              </div>
            );
          })}
          <p className="text-xs text-amber-600 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            AI annotations may alter visual appearance. Always verify against original evidence.
          </p>
        </div>
      )}
    </div>
  );
}

function RunTestFlow() {
  console.log('=== RUNTESTFLOW COMPONENT MOUNTED ===');
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  console.log('=== URL PARAM ID ===', id);
  const viewExecutionId = location.state?.viewExecutionId;
  const runTestReturnPath = location.state?.runTestReturnPath;
  // When launched from the Test Runs tab for a specific test run, reuse that
  // execution instead of creating a fresh one — keeps every run/re-run captured
  // under the same logical test run.
  const runExecutionId = location.state?.runExecutionId;
  const { selectedAuditYearId, selectedAuditYear } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);
  const {
    checkAccess: checkTestExecutionsModuleAccess,
    dialogProps: testExecutionsModuleAccessDialogProps,
  } = useRequireModuleAccess(MODULE_KEYS.TEST_EXECUTIONS, {
    hint:
      'Running or re-running a control test creates a test execution and requires the Test Executions module. Contact your organization administrator if you need access.',
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [testMappings, setTestMappings] = useState([]);
  // SCRUM-402 Phase 3: sampling context (population + C&A reports) from the
  // last analyze-stream run. Surfaced as pills on the confirm screen.
  const [samplingContext, setSamplingContext] = useState(null);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [testResults, setTestResults] = useState(null);
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // SCRUM-402 Phase 3: auditor picks evidence_type per upload batch so files
  // land in the right bucket when analyze-stream routes them (pbc -> evidence,
  // population_report -> role=population, verification -> role=verification).
  const [uploadEvidenceType, setUploadEvidenceType] = useState('pbc');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [currentMappingStep, setCurrentMappingStep] = useState(null);
  const [showContextModal, setShowContextModal] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false); // Track if execution is in progress
  const [isCancelling, setIsCancelling] = useState(false); // Track cancellation in progress
  const abortControllerRef = useRef(null); // AbortController for SSE stream

  // SCRUM-267: Testing period & population state
  const [testingPeriodStart, setTestingPeriodStart] = useState('__evergreen__');
  const [testingPeriodEnd, setTestingPeriodEnd] = useState('__evergreen__');
  // SCRUM-355: Evidence Store integration
  const [periodEvidenceCount, setPeriodEvidenceCount] = useState(0);
  const [periodEvidenceFiles, setPeriodEvidenceFiles] = useState([]);
  const [existingPeriods, setExistingPeriods] = useState([]);

  // AI Evidence Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisMessage, setAnalysisMessage] = useState('');
  const [analysisDetail, setAnalysisDetail] = useState('');
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisError, setAnalysisError] = useState(null);
  const [, setChunkInfo] = useState({ sheets: 0, pages: 0 });
  const [hasExistingMapping, setHasExistingMapping] = useState(false);
  const [, setHasExistingExecution] = useState(false);
  const [latestExecution, setLatestExecution] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeMode, setResumeMode] = useState(null); // 'execution' or 'mapping'
  const [localCheckpoint, setLocalCheckpoint] = useState(null); // localStorage checkpoint info
  const [checkpointDismissed, setCheckpointDismissed] = useState(false); // Prevent modal from re-appearing after user makes a choice
  const [additionalContext, setAdditionalContext] = useState('');
  const [selectedStepModal, setSelectedStepModal] = useState(null); // Modal for viewing individual step results
  const [isViewMode, setIsViewMode] = useState(false); // True when viewing saved results (not live execution)
  const [expandedSteps, setExpandedSteps] = useState({}); // Track expanded/collapsed mapping steps
  const [, setTestHistory] = useState([]); // All past test executions for this control

  // Evidence viewer state
  const [evidenceViewerOpen, setEvidenceViewerOpen] = useState(false);
  const [activeCitation, setActiveCitation] = useState(null);
  const [activeStepFindings, setActiveStepFindings] = useState(null);

  // NEW: Evidence mapping enhancements state
  const [fileMetadataCache, setFileMetadataCache] = useState({}); // Cache for file metadata (sheets, pages)
  const [showEditNotesModal, setShowEditNotesModal] = useState(false); // Edit notes modal visibility
  const [selectedFileForNotes, setSelectedFileForNotes] = useState(null); // File being edited in notes modal
  const [userNotes, setUserNotes] = useState(''); // Current user notes being edited
  const [, setIsLoadingMetadata] = useState(false); // Loading state for metadata fetch
  const [saveError, setSaveError] = useState(null); // Error message for save operations

  // Real data from API
  const [control, setControl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const controlUuid = useMemo(() => resolveControlUuid(control, id), [control, id]);

  const navigateBackFromRunTest = useCallback(() => {
    const viewId = controlUuid || id;
    navigate(runTestReturnPath || `/sox-controls/view/${viewId}`);
  }, [navigate, runTestReturnPath, id, controlUuid]);

  // Restore execution state from localStorage on mount - ONLY for in-progress tests
  // Completed tests don't need restoration - start fresh for new runs
  // Auto-expand missing mapping steps, collapse mapped ones
  useEffect(() => {
    if (testMappings.length > 0) {
      const expanded = {};
      testMappings.forEach((mapping) => {
        expanded[mapping.stepIndex] = mapping.files.length === 0; // Open if missing
      });
      setExpandedSteps(expanded);
    }
  }, [testMappings.length]); // Re-run when mappings count changes (initial load / re-analysis)

  useEffect(() => {
    const savedState = localStorage.getItem(`test-execution-${id}`);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        // ONLY restore if execution was actively in progress (not completed)
        // This ensures new runs start fresh, but interrupted tests can be resumed
        if (state.isExecuting) {
          console.log('Found in-progress test execution in localStorage, showing resume modal');
          // Don't auto-restore - let user decide via modal
          // The backend checkpoint check will handle showing the modal
        }
      } catch (e) {
        console.error('Error reading execution state:', e);
        localStorage.removeItem(`test-execution-${id}`);
      }
    }
  }, [id]);

  // Save execution state to localStorage ONLY during active execution
  // This creates checkpoints that can be resumed if user leaves mid-test
  useEffect(() => {
    if (isExecuting) {
      const state = {
        currentStep,
        executionLogs,
        testResults,
        isExecuting: true,
        timestamp: Date.now(),
        checkpointStep: currentStep, // Track which step user was on
        checkpointStepName: currentStep === 1 ? 'Select Evidence' :
          currentStep === 2 ? 'Map to Steps' :
            currentStep === 3 ? 'Run Test' : 'View Results'
      };
      localStorage.setItem(`test-execution-${id}`, JSON.stringify(state));
      console.log('Saved checkpoint at step', currentStep);
    }
  }, [id, currentStep, executionLogs, testResults, isExecuting]);

  // Clear localStorage when execution completes successfully
  // This ensures the next run starts completely fresh
  useEffect(() => {
    if (testResults && !testResults.error && currentStep === 4 && !isExecuting) {
      // Test completed successfully - clear the checkpoint
      localStorage.removeItem(`test-execution-${id}`);
      console.log('Cleared checkpoint - test completed successfully');
    }
  }, [id, testResults, currentStep, isExecuting]);

  // NEW: Fetch file metadata when Add File modal opens
  useEffect(() => {
    if (showAddFileModal && selectedFiles.length > 0) {
      fetchFileMetadata(selectedFiles);
    }
  }, [showAddFileModal, selectedFiles]);

  // Fetch control data from API
  useEffect(() => {
    const fetchControl = async () => {
      try {
        const userHash = localStorage.getItem('userHash');
        const response = await axiosInstance.get(`/api/sox-controls/${id}`, {
          headers: { 'Authorization': userHash || '' }
        });
        setControl(response.data);
      } catch (err) {
        console.error('Error fetching control:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchControl();
  }, [id]);

  // Fetch existing periods from Evidence Store
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const userHash = localStorage.getItem('userHash');
        const res = await axiosInstance.get(`/api/sox-controls/${id}/evidence/periods`, {
          headers: { 'Authorization': userHash || '' }
        });
        setExistingPeriods(res.data || []);
      } catch (err) {
        console.error('Failed to fetch periods:', err);
      }
    };
    if (id) fetchPeriods();
  }, [id]);

  // Auto-fetch evidence files when period changes
  useEffect(() => {
    if (!testingPeriodStart || testingPeriodStart === '__new__' || !id) {
      setPeriodEvidenceCount(0);
      setPeriodEvidenceFiles([]);
      setSelectedFiles([]);
      return;
    }
    // For dated mode, wait until both period dates are valid (avoid sending sentinel values).
    if (
      testingPeriodStart !== '__evergreen__' &&
      (!testingPeriodEnd || testingPeriodEnd === '__evergreen__' || testingPeriodEnd === '__new__')
    ) {
      setPeriodEvidenceCount(0);
      setPeriodEvidenceFiles([]);
      setSelectedFiles([]);
      return;
    }
    const fetchEvidence = async () => {
      try {
        const userHash = localStorage.getItem('userHash');
        const params = testingPeriodStart === '__evergreen__'
          ? { filter: 'evergreen' }
          : { period_start: testingPeriodStart, period_end: testingPeriodEnd };
        const res = await axiosInstance.get(`/api/sox-controls/${id}/evidence`, {
          headers: { 'Authorization': userHash || '' },
          params
        });
        const rawFiles = res.data || [];
        const files = rawFiles.map((f, i) => {
          const fname = f.filename || f.file_name || f.name || f.Filename || '';
          return {
            id: `evidence-${f.id || i}`,
            evidenceId: f.id,
            controlUploadId: f.control_upload_id,
            name: fname || `File ${i + 1}`,
            size: f.file_size ? `${(f.file_size / 1024).toFixed(1)} KB` : 'Unknown size',
            type: getFileTypeFromName(fname),
            uploadDate: f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : '',
            // SCRUM-363 Phase 1: preserve evidence_type so the analyze-stream
            // payload can tag files with role="population" when they came into
            // the control as evidence_type='population_report'. Without this
            // the FE filter at the request-build site misses them and the
            // mapper treats them as ordinary evidence.
            evidence_type: f.evidence_type,
            file_category: f.file_category || 'evidence',
          };
        });
        setPeriodEvidenceFiles(files);
        setPeriodEvidenceCount(files.length);
        // Files the control keeps as population, IPE proof or supporting documents are listed but not pre-selected:
        // they are not evidence to map (the user can still tick them).
        setSelectedFiles(files.filter(f => f.file_category === 'evidence').map(f => f.id));
      } catch (err) {
        console.error('Failed to fetch evidence:', err);
      }
    };
    fetchEvidence();
  }, [id, testingPeriodStart, testingPeriodEnd]);

  // Check for INCOMPLETE test execution - only show resume modal for interrupted runs
  // Completed tests can be viewed from the history dropdown, no modal needed
  useEffect(() => {
    const checkForCheckpoints = async () => {
      if (!control) return;

      // Don't show modal if we're viewing a specific execution from history
      if (viewExecutionId) {
        console.log('Viewing specific execution, skipping checkpoint modal');
        return;
      }

      // Don't show modal again if user already made a choice
      if (checkpointDismissed) {
        console.log('Checkpoint dismissed, not showing modal');
        return;
      }

      let hasExecution = false;
      let foundCheckpoint = null; // Use local variable since setState is async

      // First, check localStorage for in-progress checkpoint
      const savedState = localStorage.getItem(`test-execution-${id}`);
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          if (state.isExecuting) {
            console.log('Found localStorage checkpoint:', state);
            foundCheckpoint = state;
            setLocalCheckpoint(state);
          }
        } catch (e) {
          console.error('Error reading localStorage checkpoint:', e);
        }
      }

      // Check if there's existing evidence mapping we can resume from
      const existingMapping = control?.data_source_config?.evidence_mapping;
      const hasMappingData = existingMapping && existingMapping.mappings?.length > 0;

      // Check backend for latest test execution
      try {
        const userHash = localStorage.getItem('userHash');
        const response = await axiosInstance.get(
          `/api/test-executions/control/${controlUuid}/latest`,
          { headers: { 'Authorization': userHash || '' } }
        );

        if (response.data.has_execution) {
          hasExecution = true;
          const execution = response.data.execution;
          setLatestExecution(execution);
          setHasExistingExecution(true);

          // ONLY show resume modal for INCOMPLETE runs (in_progress, pending)
          // AND only if there's actually something to resume from
          if (execution.test_status === 'in_progress' || execution.test_status === 'pending') {
            // Only show modal if there's a checkpoint OR mapping data to resume from
            // Otherwise just let user start fresh without the annoying modal
            if (foundCheckpoint || hasMappingData) {
              console.log('Found incomplete execution with resumable data:', { foundCheckpoint: !!foundCheckpoint, hasMappingData });
              if (hasMappingData) {
                setHasExistingMapping(true);
              }
              setResumeMode('execution');
              setShowResumeModal(true);
              return;
            } else {
              // No checkpoint or mapping - don't show modal, just let user start fresh
              console.log('Incomplete execution but no checkpoint/mapping data - skipping modal');
            }
          } else {
            console.log('Found completed test execution, no modal needed:', execution.test_status);
          }
        }
      } catch (err) {
        console.log('No existing execution found or error:', err.message);
      }

      // Only show mapping checkpoint if no test execution exists at all
      // This helps resume evidence mapping that was never run
      if (hasMappingData && !hasExecution) {
        console.log('Found existing evidence mapping (no test run yet), showing resume modal');
        setHasExistingMapping(true);
        setResumeMode('mapping');
        setShowResumeModal(true);
      }
    };

    if (!controlUuid || !isControlUuid(controlUuid)) return;
    checkForCheckpoints();
  }, [control, controlUuid, checkpointDismissed]);

  // Fetch all test executions for history dropdown — requires control UUID
  useEffect(() => {
    const fetchTestHistory = async () => {
      if (!controlUuid || !isControlUuid(controlUuid)) return;
      try {
        const userHash = localStorage.getItem('userHash');
        const response = await axiosInstance.get(
          `/api/test-executions/control/${controlUuid}`,
          { headers: { 'Authorization': userHash || '' } }
        );
        console.log('Test history API response:', response.data);
        // API returns array directly, not wrapped in {executions: [...]}
        const executions = response.data.executions || response.data;
        if (executions && executions.length > 0) {
          console.log('Setting test history with', executions.length, 'executions');
          setTestHistory(executions);
        }
      } catch (err) {
        console.log('No test history found:', err.message);
      }
    };
    fetchTestHistory();
  }, [controlUuid]);

  // Load specific execution if viewExecutionId is passed from ViewControl
  useEffect(() => {
    const loadExecution = async () => {
      if (!viewExecutionId) return;
      try {
        const userHash = localStorage.getItem('userHash');
        const response = await axiosInstance.get(`/api/test-executions/${viewExecutionId}`, {
          headers: { 'Authorization': userHash || '' }
        });
        const exec = response.data;
        if (exec) {
          // Get execution date from various possible field names
          const execDate = exec.started_at || exec.execution_started_at || exec.created_at;
          const execDuration = exec.duration_seconds || exec.execution_duration_seconds;

          setTestResults({
            passed: exec.steps_passed || 0,
            failed: exec.steps_failed || 0,
            warnings: exec.steps_warning || 0,
            total: (exec.step_results || []).length || (exec.steps_passed + exec.steps_failed + exec.steps_warning),
            totalExceptions: exec.total_exceptions || 0,
            status: exec.overall_status === 'passed' ? 'Passed' :
              exec.overall_status === 'failed' ? 'Failed' : 'Warning',
            walkthrough: exec.walkthrough_summary,
            stepResults: exec.step_results || [],
            controlId: exec.control_id,
            executionDate: execDate ? new Date(execDate).toLocaleString() : 'N/A',
            executionDuration: execDuration || null,
            filesAnalyzed: exec.files_analyzed || [],
            test_run_id: exec.id,
            test_run_number: exec.test_run_number,
            total_population: exec.total_population,
            total_sample_size: exec.total_sample_size
          });
          setCurrentStep(4);
          setIsViewMode(true);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading execution:', err);
      }
    };
    loadExecution();
  }, [viewExecutionId]);

  // Function to load existing mapping and go to Step 2
  const resumeWithExistingMapping = () => {
    // Mark as dismissed so modal doesn't re-appear
    setCheckpointDismissed(true);

    const existingMapping = control?.data_source_config?.evidence_mapping;
    if (!existingMapping) return;

    // Get test steps for display
    const currentTestSteps = (control?.test_steps || []).map(step =>
      step.summary || step.description || step.step_description || ''
    ).filter(step => step);

    // Transform saved mapping back to UI format
    const uiMappings = currentTestSteps.map((step, index) => {
      const savedMapping = existingMapping.mappings?.find(m => m.test_step_number === index + 1);

      if (savedMapping && savedMapping.sources?.length > 0) {
        return {
          stepIndex: index,
          stepName: step,
          files: savedMapping.sources.map(source => ({
            fileId: `ai-${source.file_name}`,
            fileName: source.file_name,
            fileType: getFileTypeFromName(source.file_name),
            tabsOrPages: source.tabs_or_pages,
            usageSummary: source.usage_summary,
            populationCount: source.population_count,
            sampleCount: source.sample_count,
            samplingMethod: source.sampling_method,
            dataSummary: source.data_summary,
            preparerInfo: source.preparer_info,
            reviewerInfo: source.reviewer_info
          })),
          samplingInfo: savedMapping.sampling_info,
          unmappedExplanation: savedMapping.unmapped_reason || null,
          unmappedSuggestions: savedMapping.unmapped_suggestions || []
        };
      }

      // Return unmapped step with persisted explanation if available
      return {
        stepIndex: index,
        stepName: step,
        files: [],
        unmappedExplanation: savedMapping?.unmapped_reason || null,
        unmappedSuggestions: savedMapping?.unmapped_suggestions || []
      };
    });

    setTestMappings(uiMappings);

    // Auto-select the files that were used in the mapping
    const usedFileNames = new Set();
    existingMapping.mappings?.forEach(m => {
      m.sources?.forEach(s => usedFileNames.add(s.file_name));
    });

    const uploadedFilesList = (control?.data_source_config?.files || []);
    const fileIdsToSelect = uploadedFilesList
      .map((file, index) => usedFileNames.has(file.name) ? `file-${index}` : null)
      .filter(Boolean);

    if (fileIdsToSelect.length > 0) {
      setSelectedFiles(fileIdsToSelect);
    }

    // Go to Step 2 (Map to Steps)
    setCurrentStep(2);
    setShowResumeModal(false);
  };

  // Function to start fresh (clear mapping and stay on Step 1)
  const startFresh = async () => {
    // Mark as dismissed so modal doesn't re-appear
    setCheckpointDismissed(true);

    // Clear the localStorage checkpoint first
    localStorage.removeItem(`testExecution_${id}`);

    // Clear the mapping from backend
    try {
      const userHash = localStorage.getItem('userHash');
      await axiosInstance.put(`/api/sox-controls/${id}/evidence-mapping`, {
        evidence_mapping: null
      }, {
        headers: { 'Authorization': userHash || '' }
      });
    } catch (err) {
      console.error('Error clearing mapping:', err);
    }

    // Reset state
    setTestMappings([]);
    setSelectedFiles([]);
    setExecutionLogs([]);
    setTestResults(null);
    setHasExistingMapping(false);
    setShowResumeModal(false);
    setCurrentStep(1);

    // Also clear the mapping from local control state to prevent re-triggering the modal
    if (control?.data_source_config) {
      setControl({
        ...control,
        data_source_config: {
          ...control.data_source_config,
          evidence_mapping: null
        }
      });
    }

    // Clear any saved execution state
    localStorage.removeItem(`test-execution-${id}`);
  };

  // Helper to derive file type from filename
  const getFileTypeFromName = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['xlsx', 'xls', 'csv'].includes(ext)) return 'excel';
    if (ext === 'pdf') return 'pdf';
    if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return 'image';
    return 'document';
  };

  // Derive real data from control
  const uploadedFiles = (control?.data_source_config?.files || []).map((file, index) => ({
    id: `file-${index}`,
    name: file.name,
    size: file.size || 'Unknown size',
    type: getFileTypeFromName(file.name),
    uploadDate: 'Uploaded with control'
  }));

  // Real test steps from API - keep indices aligned (don't filter)
  const testSteps = (control?.test_steps || []).map((step, idx) =>
    step.summary || step.description || step.step_description || `Step ${idx + 1}`
  );

  // Real control metadata
  const controlData = {
    controlId: control?.control_id || id,
    title: control?.title || 'Loading...',
    frequency: control?.frequency || 'Not specified'
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'excel':
        return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
      case 'pdf':
        return <FileText className="h-5 w-5 text-red-600" />;
      case 'image':
        return <Image className="h-5 w-5 text-blue-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  // Download test results as comprehensive Excel file from backend
  const downloadResultsAsExcel = async () => {
    if (!testResults) return;

    try {
      // Build request payload - use the raw result data if available, or reconstruct from state
      const rawTestResult = testResults.rawResult || {
        control_id: testResults.controlId || control?.control_id || id,
        walkthrough: testResults.walkthrough,
        step_results: testResults.stepResults || testResults.step_results || [],
        steps_passed: testResults.passed || testResults.steps_passed || 0,
        steps_failed: testResults.failed || testResults.steps_failed || 0,
        steps_warning: testResults.warnings || testResults.steps_warning || 0,
        total_exceptions: testResults.totalExceptions || testResults.total_exceptions || 0,
        execution_started: testResults.execution_started,
        execution_completed: testResults.execution_completed,
        execution_duration_seconds: testResults.executionDuration || testResults.execution_duration_seconds,
        files_analyzed: testResults.filesAnalyzed || testResults.files_analyzed || []
      };

      const requestBody = {
        test_result: rawTestResult,
        control_title: control?.control_title || control?.title || '',
        test_steps: control?.test_steps || []
      };

      // Add evidence mapping if available (from testMappings state)
      if (testMappings && testMappings.length > 0) {
        // Reconstruct evidence mapping from testMappings
        requestBody.evidence_mapping = {
          control_id: control?.control_id || id,
          mappings: testMappings.map((mapping, idx) => ({
            test_step_number: idx + 1,
            test_step_summary: mapping.stepSummary || '',
            sources: (mapping.files || []).map(f => ({
              file_name: f.name || f.file_name || '',
              tabs_or_pages: f.tabs_or_pages || [],
              usage_summary: f.usage_summary || ''
            }))
          })),
          files_analyzed: rawTestResult.files_analyzed || []
        };
      }

      console.log('[Excel] Requesting comprehensive Excel from backend...');

      // Call backend API
      const response = await axiosInstance.post(
        '/api/test-executions/export-comprehensive-excel',
        requestBody,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${control?.control_id || id}_TestResults_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('[Excel] Download complete:', filename);

    } catch (error) {
      console.error('[Excel] Error downloading Excel:', error);
      setSaveError(resolve403ErrorMessage(error, 'Failed to generate Excel. Please try again.'));
    }
  };

  // Download results as PDF
  const downloadResultsAsPdf = async () => {
    if (!testResults) return;

    try {
      // Build request payload - same structure as Excel
      const rawTestResult = testResults.rawResult || {
        control_id: testResults.controlId || control?.control_id || id,
        walkthrough: testResults.walkthrough,
        step_results: testResults.stepResults || testResults.step_results || [],
        steps_passed: testResults.passed || testResults.steps_passed || 0,
        steps_failed: testResults.failed || testResults.steps_failed || 0,
        steps_warning: testResults.warnings || testResults.steps_warning || 0,
        total_exceptions: testResults.totalExceptions || testResults.total_exceptions || 0,
        execution_started: testResults.execution_started,
        execution_completed: testResults.execution_completed,
        execution_duration_seconds: testResults.executionDuration || testResults.execution_duration_seconds,
        files_analyzed: testResults.filesAnalyzed || testResults.files_analyzed || []
      };

      const requestBody = {
        test_result: rawTestResult,
        control_title: control?.control_title || control?.title || '',
        test_steps: control?.test_steps || []
      };

      // Add evidence mapping if available
      if (testMappings && testMappings.length > 0) {
        requestBody.evidence_mapping = {
          control_id: control?.control_id || id,
          mappings: testMappings.map((mapping, idx) => ({
            test_step_number: idx + 1,
            test_step_summary: mapping.stepSummary || '',
            sources: (mapping.files || []).map(f => ({
              file_name: f.name || f.file_name || '',
              tabs_or_pages: f.tabs_or_pages || [],
              usage_summary: f.usage_summary || ''
            }))
          })),
          files_analyzed: rawTestResult.files_analyzed || []
        };
      }

      console.log('[PDF] Requesting comprehensive PDF from backend...');

      // Call backend API
      const response = await axiosInstance.post(
        '/api/test-executions/export-comprehensive-pdf',
        requestBody,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${control?.control_id || id}_TestResults_${new Date().toISOString().split('T')[0]}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('[PDF] Download complete:', filename);

    } catch (error) {
      console.error('[PDF] Error downloading PDF:', error);
      setSaveError(resolve403ErrorMessage(error, 'Failed to generate PDF. Please try again or use the Excel download option.'));
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const proceedToMapping = async () => {
    // Use evidence files from the selected period
    const selectedFileObjects = periodEvidenceFiles.filter(f => selectedFiles.includes(f.id));
    const selectedFilenames = selectedFileObjects.map(f => f.name);

    // Transition to Step 2 (Map to Steps) immediately and show loading overlay there
    setCurrentStep(2);
    setIsAnalyzing(true);
    setAnalysisMessage('Looking up file IDs...');
    setAnalysisProgress(5);
    setAnalysisError(null);

    // Get file IDs from evidence store (controlUploadId) or lookup by filename
    let fileIds = selectedFileObjects
      .map(f => f.controlUploadId)
      .filter(id => id != null && typeof id === 'number');

    if (fileIds.length === 0) {
      // Fallback: lookup file IDs by filename
      try {
        const userHash = localStorage.getItem('userHash');
        const lookupResponse = await axiosInstance.post('/api/control-uploads/lookup-by-filename', {
          filenames: selectedFilenames
        }, {
          headers: { 'Authorization': userHash || '' }
        });
        const lookupResults = lookupResponse.data?.files || [];
        fileIds = lookupResults.filter(r => r.found).map(r => r.id);
        console.log('File lookup results:', lookupResults);
      } catch (err) {
        console.error('Error looking up files:', err);
        if (err?.response?.status === 403) {
          setAnalysisError(resolve403ErrorMessage(err, 'Failed to look up files'));
          setIsAnalyzing(false);
          setAnalysisMessage('');
          setAnalysisProgress(0);
          return;
        }
      }
    }

    // Use round-robin fallback if no file IDs found
    if (fileIds.length === 0) {
      console.log('No file IDs found in database, using round-robin mapping');
      const autoMappings = testSteps.map((step, index) => {
        if (selectedFileObjects.length > 0) {
          const fileIndex = index % selectedFileObjects.length;
          const file = selectedFileObjects[fileIndex];
          return {
            stepIndex: index,
            stepName: step,
            files: [{
              fileId: file.id,
              fileName: file.name,
              fileType: file.type
            }]
          };
        }
        return { stepIndex: index, stepName: step, files: [] };
      });
      setTestMappings(autoMappings);
      setAnalysisError('AI analysis skipped - files not stored in database. Using automatic mapping.');
      setIsAnalyzing(false);
      setAnalysisMessage('');
      setAnalysisProgress(0);
      return;
    }

    // Prepare test steps for AI analysis - include FULL description for context
    const testStepsData = (control?.test_steps || []).map((step, idx) => ({
      step_number: idx + 1,
      summary: step.summary || step.description || step.step_description || '',
      description: step.description || step.step_description || '',  // Full description for AI context
      key_points: step.key_points || [],
      required_evidence: step.required_evidence || []
    }));

    // Update progress - file IDs found, now starting AI analysis
    setAnalysisMessage('Starting AI analysis...');
    setAnalysisProgress(10);
    setChunkInfo({ sheets: 0, pages: 0 }); // Reset chunk info

    try {
      // SCRUM-363: build typed files[] with role metadata. Population-role
      // files are identified via selectedFileObjects metadata (evidence_type
      // set by the auditor in the upload modal's "Upload as" dropdown).
      const populationFileIds = new Set(
        (selectedFileObjects || [])
          .filter(f => f && (f.evidence_type === 'population_report' || f.role === 'population'))
          .map(f => f.controlUploadId)
          .filter(id => typeof id === 'number')
      );
      // SCRUM-402 Phase 3: files tagged evidence_type='verification' flow to
      // the Completeness & Accuracy agents instead of the step mapper.
      const verificationFileIds = new Set(
        (selectedFileObjects || [])
          .filter(f => f && (f.evidence_type === 'verification' || f.role === 'verification'))
          .map(f => f.controlUploadId)
          .filter(id => typeof id === 'number')
      );
      const typedFiles = fileIds.map(fid => {
        let role = 'evidence';
        if (populationFileIds.has(fid)) role = 'population';
        else if (verificationFileIds.has(fid)) role = 'verification';
        return { file_id: fid, role };
      });

      // Use SSE streaming for real-time progress updates via Go backend proxy
      const response = await axiosInstance.post('/api/evidence/analyze-stream', {
        control_id: control?.control_id || id,
        control_title: control?.title,
        control_objective: control?.control_objective,
        test_steps: testStepsData,
        file_ids: fileIds,       // legacy field kept for back-compat
        files: typedFiles,        // SCRUM-363 typed shape, preferred by router
        mode: 'full',
      }, {
        adapter: 'fetch',
        responseType: 'stream',
        validateStatus: () => true
      });

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.data.getReader();
      const decoder = new TextDecoder();
      let result = null;
      let buffer = ''; // Buffer for incomplete SSE events

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Append new data to buffer
        buffer += decoder.decode(value, { stream: true });

        // Parse complete SSE events (format: "data: {...}\n\n")
        // Split by \n\n but keep track of incomplete events
        const events = buffer.split('\n\n');

        // Keep the last part if it doesn't end with \n\n (incomplete event)
        if (!buffer.endsWith('\n\n') && events.length > 0) {
          buffer = events.pop(); // Save incomplete event back to buffer
        } else {
          buffer = ''; // All events are complete
        }

        for (const event of events) {
          if (!event.startsWith('data: ')) continue;

          try {
            const eventData = JSON.parse(event.replace('data: ', ''));

            // Update progress from real API events
            if (eventData.message) setAnalysisMessage(eventData.message);
            if (eventData.detail) setAnalysisDetail(eventData.detail);
            if (eventData.progress !== undefined) setAnalysisProgress(eventData.progress);

            // Update chunk counts when available
            if (eventData.excel_sheets !== undefined || eventData.pdf_pages !== undefined) {
              setChunkInfo({
                sheets: eventData.excel_sheets || 0,
                pages: eventData.pdf_pages || 0
              });
            }

            // Handle completion
            if (eventData.stage === 'complete' && eventData.result) {
              result = eventData.result;
              console.log('Received complete event with result:', Object.keys(eventData.result));
            }

            // Handle errors
            if (eventData.stage === 'error') {
              throw new Error(eventData.message);
            }
          } catch (parseErr) {
            // Log parse errors for debugging but continue
            console.warn('SSE parse warning:', parseErr.message, 'Event length:', event.length);
          }
        }
      }

      if (!result) {
        throw new Error('No result received from analysis');
      }

      setAnalysisProgress(100);
      setAnalysisMessage('Complete!');

      // Transform AI response to UI mapping format
      const aiMappings = testSteps.map((step, index) => {
        const aiMapping = result.mappings?.find(m => m.test_step_number === index + 1);

        if (aiMapping && aiMapping.sources?.length > 0) {
          return {
            stepIndex: index,
            stepName: step,
            files: aiMapping.sources.map(source => ({
              fileId: `ai-${source.file_name}`,
              fileName: source.file_name,
              fileType: getFileTypeFromName(source.file_name),
              tabsOrPages: source.tabs_or_pages,
              usageSummary: source.usage_summary,
              // Per-source sampling data from multi-stage analysis
              populationCount: source.population_count,
              sampleCount: source.sample_count,
              samplingMethod: source.sampling_method,
              dataSummary: source.data_summary,
              preparerInfo: source.preparer_info,
              reviewerInfo: source.reviewer_info
            })),
            samplingInfo: aiMapping.sampling_info
          };
        }

        return {
          stepIndex: index,
          stepName: step,
          files: [],
          unmappedExplanation: aiMapping?.unmapped_reason || null,
          unmappedSuggestions: aiMapping?.unmapped_suggestions || []
        };
      });

      setTestMappings(aiMappings);

      // SCRUM-402 Phase 3: capture sampling_context for the C&A pills on the confirm screen.
      if (result?.sampling_context) {
        setSamplingContext(result.sampling_context);
      }

      // Save the mapping to backend for later use by test agent
      const userHash = localStorage.getItem('userHash');
      await axiosInstance.put(`/api/sox-controls/${id}/evidence-mapping`, {
        evidence_mapping: result
      }, {
        headers: { 'Authorization': userHash || '' }
      });

      // Automatically generate unmapped explanations for steps with no files
      // BUT only if the AI didn't already provide an explanation
      aiMappings.forEach((mapping, index) => {
        if (mapping.files.length === 0 && !mapping.unmappedExplanation) {
          // Use setTimeout to avoid blocking and allow UI to update
          setTimeout(() => generateUnmappedExplanation(index), 100 * index);
        }
      });
    } catch (err) {
      console.error('Error analyzing evidence:', err);
      setAnalysisError(err.response?.data?.detail || err.message || 'Failed to analyze evidence');

      // Fallback to round-robin mapping on error
      const selectedFileObjects = periodEvidenceFiles.filter(f => selectedFiles.includes(f.id));
      const autoMappings = testSteps.map((step, index) => {
        if (selectedFileObjects.length > 0) {
          const fileIndex = index % selectedFileObjects.length;
          const file = selectedFileObjects[fileIndex];
          return {
            stepIndex: index,
            stepName: step,
            files: [{
              fileId: file.id,
              fileName: file.name,
              fileType: file.type
            }]
          };
        }
        return { stepIndex: index, stepName: step, files: [] };
      });
      setTestMappings(autoMappings);
    } finally {
      setIsAnalyzing(false);
      setAnalysisMessage('');
      setAnalysisDetail('');
      setAnalysisProgress(0);
      setChunkInfo({ sheets: 0, pages: 0 });
    }
  };

  const addFileToMapping = async (stepIndex, file) => {
    // Update local state first
    const updatedMappings = testMappings.map(mapping => {
      if (mapping.stepIndex === stepIndex) {
        const existingFile = mapping.files.find(f => f.fileId === file.id);
        if (existingFile) {
          return mapping;
        }
        // Clear unmapped explanation when file is added
        return {
          ...mapping,
          files: [...mapping.files, {
            fileId: file.id,
            fileName: file.name,
            fileType: file.type
          }],
          unmappedExplanation: undefined,
          unmappedSuggestions: undefined,
          isLoadingExplanation: false
        };
      }
      return mapping;
    });

    setTestMappings(updatedMappings);
    setShowAddFileModal(false);
    setCurrentMappingStep(null);

    // Save updated mapping to backend so notes can be saved later
    try {
      const userHash = localStorage.getItem('userHash');
      // Convert UI mapping format to backend evidence_mapping format
      const evidenceMapping = {
        control_id: control?.control_id || id,
        mappings: updatedMappings.map(m => ({
          test_step_number: m.stepIndex + 1,
          test_step_summary: m.stepName,
          sources: m.files.map(f => ({
            file_name: f.fileName,
            tabs_or_pages: f.tabsOrPages || [],
            usage_summary: f.usageSummary || '',
            user_notes: f.userNotes || null
          }))
        })),
        analysis_timestamp: new Date().toISOString()
      };

      await axiosInstance.put(`/api/sox-controls/${id}/evidence-mapping`, {
        evidence_mapping: evidenceMapping
      }, {
        headers: { 'Authorization': userHash || '' }
      });
      console.log('Manual mapping saved to backend');
    } catch (err) {
      console.error('Error saving manual mapping:', err);
      // Continue - local state is updated even if backend save fails
    }
  };

  // NEW: Fetch file metadata (sheet names, page counts)
  const fetchFileMetadata = async (fileIds) => {
    if (fileIds.length === 0) return;

    // Check if already cached
    const uncachedIds = fileIds.filter(id => !fileMetadataCache[id]);
    if (uncachedIds.length === 0) return;

    setIsLoadingMetadata(true);
    try {
      const response = await axiosInstance.post('/api/files/extract-metadata', {
        file_ids: uncachedIds
      });

      const newCache = { ...fileMetadataCache };
      response.data.metadata.forEach(meta => {
        newCache[meta.file_id] = meta;
      });
      setFileMetadataCache(newCache);
      console.log('Fetched metadata for files:', uncachedIds);
    } catch (err) {
      console.error('Error fetching file metadata:', err);
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  // NEW: Open edit notes modal
  const openEditNotesModal = (stepIndex, file) => {
    setSaveError(null); // Clear any previous errors
    setSelectedFileForNotes({
      stepIndex,
      fileName: file.fileName,
      usageSummary: file.usageSummary,
      currentNotes: file.userNotes || ''
    });
    setUserNotes(file.userNotes || '');
    setShowEditNotesModal(true);
  };

  // NEW: Save user notes
  const saveUserNotes = async () => {
    if (!selectedFileForNotes) return;

    try {
      const userHash = localStorage.getItem('userHash');
      await axiosInstance.patch(
        `/api/sox-controls/${id}/evidence-mapping/notes`,
        {
          step_number: selectedFileForNotes.stepIndex + 1,
          file_name: selectedFileForNotes.fileName,
          user_notes: userNotes
        },
        { headers: { 'Authorization': userHash || '' } }
      );

      // Update local state
      setTestMappings(prev => prev.map(mapping => {
        if (mapping.stepIndex === selectedFileForNotes.stepIndex) {
          return {
            ...mapping,
            files: mapping.files.map(f => {
              if (f.fileName === selectedFileForNotes.fileName) {
                return { ...f, userNotes: userNotes };
              }
              return f;
            })
          };
        }
        return mapping;
      }));

      setShowEditNotesModal(false);
      setSelectedFileForNotes(null);
      setUserNotes('');
      setSaveError(null);
      console.log('User notes saved successfully');
    } catch (err) {
      console.error('Error saving user notes:', err);
      setSaveError(resolve403ErrorMessage(err, 'Failed to save notes. Please try again.'));
    }
  };

  // NEW: Generate unmapped explanation
  const generateUnmappedExplanation = async (stepIndex) => {
    const mapping = testMappings.find(m => m.stepIndex === stepIndex);
    if (!mapping) return;

    // Set loading state
    setTestMappings(prev => prev.map(m => {
      if (m.stepIndex === stepIndex) {
        return { ...m, isLoadingExplanation: true };
      }
      return m;
    }));

    try {
      const testStep = control.test_steps[stepIndex];
      // Use all evidence files for better explanation
      const uploadedFileNames = periodEvidenceFiles.map(f => f.name);

      // If no uploaded files exist, skip explanation
      if (uploadedFileNames.length === 0) {
        console.log('No uploaded files found, skipping unmapped explanation');
        setTestMappings(prev => prev.map(m => {
          if (m.stepIndex === stepIndex) {
            return { ...m, isLoadingExplanation: false };
          }
          return m;
        }));
        return;
      }

      const response = await axiosInstance.post('/api/evidence/explain-unmapped', {
        control_id: id,
        test_step: testStep,
        uploaded_files: uploadedFileNames
      });

      // Update with explanation
      setTestMappings(prev => prev.map(m => {
        if (m.stepIndex === stepIndex) {
          return {
            ...m,
            unmappedExplanation: response.data.explanation,
            unmappedSuggestions: response.data.suggestions || [],
            isLoadingExplanation: false
          };
        }
        return m;
      }));
      console.log('Generated unmapped explanation for step', stepIndex + 1);

      // Persist the explanation to database
      try {
        const userHash = localStorage.getItem('userHash');
        const existingMapping = control?.data_source_config?.evidence_mapping;

        if (existingMapping && existingMapping.mappings) {
          // Update the specific mapping with unmapped_reason
          const updatedMappings = existingMapping.mappings.map(m => {
            if (m.test_step_number === stepIndex + 1) {
              return {
                ...m,
                unmapped_reason: response.data.explanation,
                unmapped_suggestions: response.data.suggestions || []
              };
            }
            return m;
          });

          await axiosInstance.put(`/api/sox-controls/${id}/evidence-mapping`, {
            evidence_mapping: {
              ...existingMapping,
              mappings: updatedMappings
            }
          }, {
            headers: { 'Authorization': userHash || '' }
          });
          console.log('Saved unmapped explanation to database');
        }
      } catch (saveErr) {
        console.error('Failed to persist unmapped explanation:', saveErr);
        // Non-critical error - explanation still shows in UI
      }
    } catch (err) {
      console.error('Error generating unmapped explanation:', err);
      setTestMappings(prev => prev.map(m => {
        if (m.stepIndex === stepIndex) {
          return { ...m, isLoadingExplanation: false };
        }
        return m;
      }));
    }
  };

  // Handle uploading new evidence files
  const handleUploadFiles = async () => {
    if (uploadFiles.length === 0) return;

    setIsUploading(true);
    try {
      const userHash = localStorage.getItem('userHash');

      // Upload to Evidence Store with period if selected
      if (
        testingPeriodStart &&
        testingPeriodStart !== '__new__' &&
        testingPeriodStart !== '__evergreen__' &&
        testingPeriodEnd &&
        testingPeriodEnd !== '__evergreen__'
      ) {
        for (const file of uploadFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('testing_period_start', testingPeriodStart);
          formData.append('testing_period_end', testingPeriodEnd);
          formData.append('evidence_type', uploadEvidenceType);
          await axiosInstance.post(`/api/sox-controls/${id}/evidence/upload`, formData, {
            headers: { 'Authorization': userHash || '', 'Content-Type': 'multipart/form-data' }
          });
        }
        // Re-fetch evidence files for this period
        const res = await axiosInstance.get(`/api/sox-controls/${id}/evidence`, {
          headers: { 'Authorization': userHash || '' },
          params: { period_start: testingPeriodStart, period_end: testingPeriodEnd }
        });
        const files = (res.data || []).map((f, i) => ({
          id: `evidence-${f.id || i}`,
          evidenceId: f.id,
          controlUploadId: f.control_upload_id,
          name: f.filename || f.file_name || f.name || 'Unknown',
          size: f.file_size ? `${(f.file_size / 1024).toFixed(1)} KB` : 'Unknown size',
          type: getFileTypeFromName(f.filename || f.file_name || f.name || ''),
          uploadDate: f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : '',
          evidence_type: f.evidence_type,
          file_category: f.file_category || 'evidence',
        }));
        setPeriodEvidenceFiles(files);
        setPeriodEvidenceCount(files.length);
        setSelectedFiles(files.filter(f => f.file_category === 'evidence').map(f => f.id));
        // Also refresh periods list
        const periodsRes = await axiosInstance.get(`/api/sox-controls/${id}/evidence/periods`, {
          headers: { 'Authorization': userHash || '' }
        });
        setExistingPeriods(periodsRes.data || []);
      } else if (testingPeriodStart === '__evergreen__' || testingPeriodEnd === '__evergreen__') {
        for (const file of uploadFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('evidence_type', uploadEvidenceType);
          await axiosInstance.post(`/api/sox-controls/${id}/evidence/upload`, formData, {
            headers: { 'Authorization': userHash || '', 'Content-Type': 'multipart/form-data' }
          });
        }
        // Re-fetch evergreen evidence only (NULL period rows)
        const res = await axiosInstance.get(`/api/sox-controls/${id}/evidence`, {
          headers: { 'Authorization': userHash || '' },
          params: { filter: 'evergreen' }
        });
        const files = (res.data || []).map((f, i) => ({
          id: `evidence-${f.id || i}`,
          evidenceId: f.id,
          controlUploadId: f.control_upload_id,
          name: f.filename || f.file_name || f.name || 'Unknown',
          size: f.file_size ? `${(f.file_size / 1024).toFixed(1)} KB` : 'Unknown size',
          type: getFileTypeFromName(f.filename || f.file_name || f.name || ''),
          uploadDate: f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : '',
          evidence_type: f.evidence_type,
          file_category: f.file_category || 'evidence',
        }));
        setPeriodEvidenceFiles(files);
        setPeriodEvidenceCount(files.length);
        setSelectedFiles(files.filter(f => f.file_category === 'evidence').map(f => f.id));
        const periodsRes = await axiosInstance.get(`/api/sox-controls/${id}/evidence/periods`, {
          headers: { 'Authorization': userHash || '' }
        });
        setExistingPeriods(periodsRes.data || []);
      } else {
        // Fallback: upload to control data-files
        const formData = new FormData();
        uploadFiles.forEach(file => formData.append('files', file));
        await axiosInstance.post(`/api/sox-controls/${id}/data-files`, formData, {
          headers: { 'Authorization': userHash || '', 'Content-Type': 'multipart/form-data' }
        });
        const response = await axiosInstance.get(`/api/sox-controls/${id}`, {
          headers: { 'Authorization': userHash || '' }
        });
        setControl(response.data);
      }

      setShowUploadModal(false);
      setUploadFiles([]);
    } catch (err) {
      console.error('Error uploading files:', err);
      setSaveError(resolve403ErrorMessage(err, 'Failed to upload files. Please try again.'));
    } finally {
      setIsUploading(false);
    }
  };

  const removeFileFromMapping = (stepIndex, fileId) => {
    setTestMappings(prev => prev.map(mapping => {
      if (mapping.stepIndex === stepIndex) {
        return {
          ...mapping,
          files: mapping.files.filter(f => f.fileId !== fileId)
        };
      }
      return mapping;
    }));
  };

  // Clear existing mapping and go back to Step 1 to re-run analysis
  const clearMappingAndRerun = async () => {
    // Clear the mapping from backend
    try {
      const userHash = localStorage.getItem('userHash');
      await axiosInstance.put(`/api/sox-controls/${id}/evidence-mapping`, {
        evidence_mapping: null
      }, {
        headers: { 'Authorization': userHash || '' }
      });
    } catch (err) {
      console.error('Error clearing mapping:', err);
      // Continue anyway - UI will reset
    }

    // Reset UI state
    setTestMappings([]);
    setSelectedFiles([]);
    setHasExistingMapping(false);
    setAnalysisError(null);
    setCurrentStep(1);
  };

  // State for results tab navigation
  const [activeResultTab, setActiveResultTab] = useState('walkthrough');

  // Handle re-running the test with additional context
  const handleRerunWithContext = () => {
    if (!checkTestExecutionsModuleAccess()) return;
    setShowContextModal(false);
    // Store context for use in execution - will be used in startExecution
    startExecution(additionalContext);
    setAdditionalContext('');
  };

  const startExecution = async (context = null) => {
    if (!checkTestExecutionsModuleAccess()) return;
    setCurrentStep(3);
    setTestResults(null);
    setIsExecuting(true); // Mark execution as in progress
    setIsViewMode(false); // Reset view mode since this is a live execution

    // Initialize ALL steps but mark them as invisible until they start
    const initialLogs = [
      {
        step: 0,
        isSystem: true,
        status: 'in-progress',
        message: 'Starting test execution...',
        timestamp: new Date().toLocaleTimeString()
      },
      ...testSteps.map((stepDesc, idx) => ({
        step: idx + 1,
        stepName: stepDesc,
        status: 'pending',
        resultStatus: null,
        message: 'Waiting to start...',
        timestamp: new Date().toLocaleTimeString(),
        isSystem: false,
        visible: false // HIDDEN until step actually starts
      }))
    ];
    setExecutionLogs(initialLogs);

    try {
      // Get the UUID control ID - use the URL param id which is the UUID
      const executionControlUuid = resolveControlUuid(control, id);
      if (!executionControlUuid || !isControlUuid(executionControlUuid)) {
        throw new Error('Control UUID is required to run tests');
      }

      // Clear any stale localStorage before starting new execution
      localStorage.removeItem(`test-execution-${id}`);

      // Create test execution record in backend FIRST (for persistence).
      // Do NOT override Authorization here — the axios interceptor already
      // attaches the Bearer access_token + X-User-Id + X-Organization-ID
      // expected by OrganizationMiddleware. Passing the raw userHash as a
      // bearer value caused silent 401s, a null executionId, and runs that
      // never persisted to test_executions (so Test Runs tab stayed empty
      // and the Evidence Annotations cache had no row to hang off).
      //
      // Also: only forward testing_period_* when they're real ISO dates.
      // "__evergreen__" / "__new__" sentinels must not reach the backend —
      // its `time.Parse("2006-01-02", ...)` silently drops them to NULL,
      // which then collides with the SCRUM-316
      // `idx_test_executions_one_current_per_period` partial unique index
      // (every evergreen create maps to the same synthetic 1970-01-01 key).
      const isRealDate = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
      let executionId = runExecutionId || null;
      try {
        if (executionId) {
          // Reuse the test run the user launched from — don't create a new one.
          console.log('Reusing existing test execution record:', executionId);
        } else {
        const createResponse = await axiosInstance.post('/api/test-executions', {
          sox_control_id: executionControlUuid,
          control_id: control?.control_id,
          evidence_mapping_snapshot: control?.data_source_config?.evidence_mapping,
          testing_period_start: isRealDate(testingPeriodStart) ? testingPeriodStart : undefined,
          testing_period_end: isRealDate(testingPeriodEnd) ? testingPeriodEnd : undefined,
          audit_year_id: selectedAuditYearId || undefined,
        });
        executionId = createResponse.data.id;
        // Promote new execution into React state so activeExecutionId resolves
        // to it. Without this, latestExecution stays at the prior run (or null
        // for first runs), so InlineEditableField.save() PATCHes /step-field on
        // the wrong execution (or fails with "No active execution"). That's the
        // root cause of "I added an Auditor comment and it didn't persist."
        setLatestExecution(createResponse.data);
        console.log('Created test execution record:', executionId);
        }
      } catch (err) {
        console.error('Failed to create execution record:', err);
        if (err?.response?.status === 403) {
          setIsExecuting(false);
          setExecutionLogs((prev) => [
            ...prev.filter((l) => !l.isSystem),
            {
              step: 0,
              stepName: 'Error',
              status: 'failed',
              timestamp: new Date().toLocaleTimeString(),
              message: resolve403ErrorMessage(err, 'Failed to create test execution'),
              isSystem: true,
            },
          ]);
          return;
        }
        // Continue anyway - results won't be persisted but test can run
      }

      // Create AbortController for cancellation support
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Use SSE streaming endpoint via Go backend proxy.
      // Timeout bumped to 20 min: real runs with annotation pre-gen +
      // walkthrough synthesis can reach 10–12 min, and a 10 min axios
      // timeout was aborting the reader mid-stream — the JS runtime then
      // surfaced it as a cryptic "Iterator result undefined is not an
      // object" TypeError, which fell into the catch block and wiped
      // the result to an error state. Also normalize sentinel periods
      // to null so BE `time.Parse` doesn't silently drop them.
      const isoDate = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) ? v : null;
      const response = await axiosInstance.post('/api/test-executions/execute-stream', {
        control_id: executionControlUuid,
        additional_context: context || additionalContext || null,
        execution_id: executionId,
        testing_period_start: isoDate(testingPeriodStart),
        testing_period_end: isoDate(testingPeriodEnd),
      }, {
        adapter: 'fetch',
        responseType: 'stream',
        validateStatus: () => true,
        timeout: 1200000,
        signal: abortController.signal
      });

      if (response.status === 403) {
        let message = 'Test execution failed';
        try {
          const text = await new Response(response.data).text();
          const parsed = JSON.parse(text);
          message = parsed.error || parsed.message || parsed.detail || message;
        } catch { /* keep default */ }
        throw new Error(message);
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.data.getReader();
      const decoder = new TextDecoder();
      let finalResult = null;
      let buffer = ''; // Buffer for incomplete SSE events

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Append new data to buffer
        buffer += decoder.decode(value, { stream: true });

        // Parse complete SSE events - handle both \r\n\r\n and \n\n as delimiters
        // SSE spec allows \r\n, \r, or \n as line endings
        // Normalize line endings first
        const normalizedBuffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const events = normalizedBuffer.split('\n\n');

        // Keep the last part if it doesn't end with \n\n (incomplete event)
        if (!normalizedBuffer.endsWith('\n\n') && events.length > 0) {
          buffer = events.pop(); // Save incomplete event back to buffer (normalized)
        } else {
          buffer = ''; // All events are complete
        }

        for (const eventText of events.filter(e => e.trim())) {
          // Parse event type and data
          const lines = eventText.split('\n');
          let eventType = 'message';
          let eventData = null;

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.replace('event: ', '').trim();
            } else if (line.startsWith('data: ')) {
              try {
                eventData = JSON.parse(line.replace('data: ', ''));
              } catch (e) {
                // IMPROVED: Log to console.error instead of console.debug for visibility
                console.error('SSE parse error:', e.message, 'Raw line:', line.substring(0, 100));
                // Add error to execution logs so user can see it
                setExecutionLogs(prev => [...prev, {
                  step: 0,
                  isSystem: true,
                  status: 'warning',
                  message: `Parse warning: ${e.message}`,
                  timestamp: new Date().toLocaleTimeString()
                }]);
              }
            }
          }

          if (!eventData) continue;

          // Handle progress events
          if (eventType === 'progress') {
            // Backend sends "step_number" - consistent with schema
            const { stage, progress, message, step_number, step_result } = eventData;

            // COMPREHENSIVE DEBUG LOGGING - Log ALL events to diagnose issue
            console.log('[SSE] Progress event:', {
              stage,
              progress,
              step_number,
              hasStepResult: !!step_result,
              resultStatus: step_result?.status,
              message: message?.substring(0, 100)
            });

            // Update execution logs based on stage
            setExecutionLogs(prev => {
              const newLogs = [...prev];

              // If we have a step number, update that specific step
              const step = step_number;
              if (step !== undefined && step > 0) {
                const existingIdx = newLogs.findIndex(l => l.step === step && !l.isSystem);

                // Determine status based on stage and step_result
                let stepStatus = 'in-progress';
                let resultStatus = null;
                let findings = null;
                let exceptions = null;

                if (stage === 'step_complete' && step_result) {
                  // We have actual results - use them!
                  resultStatus = step_result.status; // passed/failed/warning/timed_out
                  stepStatus = resultStatus === 'passed' ? 'completed' :
                    resultStatus === 'failed' ? 'failed' :
                      resultStatus === 'warning' ? 'warning' :
                        resultStatus === 'timed_out' ? 'failed' : 'completed';
                  findings = step_result.findings_summary;
                  exceptions = step_result.exceptions_found;
                } else if (message?.toLowerCase().includes('completed')) {
                  stepStatus = 'completed';
                }

                const logEntry = {
                  step,
                  stepName: testSteps[step - 1] || `Step ${step}`,
                  status: stepStatus,
                  resultStatus, // passed/failed/warning from actual result
                  findings,
                  exceptions,
                  timestamp: new Date().toLocaleTimeString(),
                  message: message,
                  progress,
                  stepResult: step_result, // Store full result for details
                  parsedResult: step_result, // Also store as parsedResult for modal
                  visible: true // Make step visible when it receives an event
                };

                if (existingIdx >= 0) {
                  // IMPORTANT: Don't overwrite completed results with in-progress status
                  // Only update if: 1) we have new results, or 2) existing entry isn't complete
                  const existingLog = newLogs[existingIdx];
                  const existingHasResult = existingLog.resultStatus && ['passed', 'failed', 'warning'].includes(existingLog.resultStatus);
                  const newHasResult = resultStatus && ['passed', 'failed', 'warning'].includes(resultStatus);

                  if (newHasResult || !existingHasResult) {
                    newLogs[existingIdx] = { ...existingLog, ...logEntry };
                  }
                  // Otherwise keep existing completed result
                } else {
                  // Insert step logs in order (after system log if present)
                  const insertIdx = newLogs.findIndex(l => !l.isSystem && l.step > step);
                  if (insertIdx >= 0) {
                    newLogs.splice(insertIdx, 0, logEntry);
                  } else {
                    newLogs.push(logEntry);
                  }
                }
              }

              // Always update system log with current stage/progress
              const systemLog = {
                step: 0,
                stepName: stage === 'preparing' ? 'Preparation' :
                  stage === 'analyzing' ? 'Analysis' :
                    stage === 'step_complete' ? 'Analysis' :
                      stage === 'parsing' ? 'Parsing Results' :
                        stage === 'synthesizing' ? 'Generating Summary' :
                          stage === 'finalizing' ? 'Finalizing' : 'Processing',
                status: 'in-progress',
                timestamp: new Date().toLocaleTimeString(),
                message,
                progress,
                isSystem: true
              };

              // Replace or add system log at the beginning
              const sysIdx = newLogs.findIndex(l => l.isSystem);
              if (sysIdx >= 0) {
                newLogs[sysIdx] = systemLog;
              } else {
                newLogs.unshift(systemLog);
              }

              return newLogs;
            });
          }

          // Handle timeout/cancel events
          if (eventData.stage === 'timed_out' || eventData.stage === 'cancelled') {
            setExecutionLogs(prev => [
              ...prev,
              {
                step: 0,
                isSystem: true,
                status: 'failed',
                message: eventData.message || (eventData.stage === 'timed_out' ? 'Test execution timed out' : 'Test execution cancelled'),
                timestamp: new Date().toLocaleTimeString()
              }
            ]);
          }

          // Handle completion event - check BOTH eventType AND stage inside data
          // Backend may send as event: complete OR as data: {stage: 'complete', ...}
          const isCompleteEvent = (eventType === 'complete' || eventData.stage === 'complete') && eventData.result;
          if (isCompleteEvent) {
            finalResult = eventData.result;
            console.log('Test execution complete event received, eventType:', eventType, 'stage:', eventData.stage);

            // Transform result and set testResults immediately
            const result = eventData.result;
            const stepResults = result.step_results || [];

            setTestResults({
              // Summary counts
              passed: result.steps_passed || 0,
              failed: result.steps_failed || 0,
              warnings: result.steps_warning || 0,
              total: stepResults.length,
              totalExceptions: result.total_exceptions || 0,

              // Overall status
              status: result.walkthrough?.overall_status === 'passed' ? 'Passed' :
                result.walkthrough?.overall_status === 'failed' ? 'Failed' : 'Warning',

              // Walkthrough / Executive Summary
              walkthrough: result.walkthrough,

              // Per-step detailed results
              stepResults: stepResults,

              // Metadata
              controlId: result.control_id,
              executionDate: new Date(result.execution_started).toLocaleDateString(),
              executionTime: new Date(result.execution_started).toLocaleTimeString(),
              executionDuration: result.execution_duration_seconds,
              filesAnalyzed: result.files_analyzed || [],

              // Raw result for Excel export (contains all original data)
              rawResult: result
            });

            // Update system log to show completion
            setExecutionLogs(prev => {
              const newLogs = [...prev];

              const completionLog = {
                step: 0,
                isSystem: true,
                status: 'completed',
                message: `Test execution complete! ${result.steps_passed || 0} passed, ${result.steps_failed || 0} failed, ${result.steps_warning || 0} warnings`,
                timestamp: new Date().toLocaleTimeString()
              };

              const sysIdx = newLogs.findIndex(l => l.isSystem);
              if (sysIdx >= 0) {
                newLogs[sysIdx] = completionLog;
              } else {
                newLogs.unshift(completionLog);
              }

              return newLogs;
            });
          }

          // Handle error event
          if (eventType === 'error') {
            throw new Error(eventData.message || 'Test execution failed');
          }
        }
      }

      if (!finalResult) {
        throw new Error('No result received from test execution');
      }

      // Transform API result to UI format
      const result = finalResult;
      const stepResults = result.step_results || [];

      // Update execution logs with final results (consistent with streaming format)
      const finalLogs = stepResults.map((sr, idx) => ({
        step: sr.step_number || idx + 1,
        stepName: sr.step_description || testSteps[idx] || `Step ${idx + 1}`,
        status: sr.status === 'passed' ? 'completed' :
          sr.status === 'failed' ? 'failed' : 'warning',
        resultStatus: sr.status, // passed/failed/warning - used by UI badges
        findings: sr.findings_summary,
        exceptions: sr.exceptions_found || 0,
        timestamp: new Date().toLocaleTimeString(),
        message: sr.findings_summary || 'Analysis complete',
        stepResult: sr, // Full result for detailed view (consistent naming)
        parsedResult: sr, // Also store as parsedResult for modal
        result: sr // Keep for backwards compat
      }));

      setExecutionLogs(finalLogs);

      // Set comprehensive test results
      setTestResults({
        // Summary counts
        passed: result.steps_passed || 0,
        failed: result.steps_failed || 0,
        warnings: result.steps_warning || 0,
        total: stepResults.length,
        totalExceptions: result.total_exceptions || 0,

        // Overall status
        status: result.walkthrough?.overall_status === 'passed' ? 'Passed' :
          result.walkthrough?.overall_status === 'failed' ? 'Failed' : 'Warning',

        // Walkthrough / Executive Summary
        walkthrough: result.walkthrough,

        // Per-step detailed results
        stepResults: stepResults,

        // Metadata
        controlId: result.control_id,
        executionDate: new Date(result.execution_started).toLocaleDateString(),
        executionTime: new Date(result.execution_started).toLocaleTimeString(),
        executionDuration: result.execution_duration_seconds,
        filesAnalyzed: result.files_analyzed || []
      });

      setIsExecuting(false); // Execution completed successfully

      // IMPROVED: Auto-advance to results with explicit logging
      console.log('Test complete, auto-navigating to results in 1 second...');
      setTimeout(() => {
        console.log('Navigating to Step 4 (Results)');
        setCurrentStep(4);
      }, 1000);

    } catch (err) {
      console.error('Test execution error:', err);
      setIsExecuting(false); // Execution stopped due to error

      // Show error in logs
      setExecutionLogs(prev => [
        ...prev.filter(l => !l.isSystem),
        {
          step: 0,
          stepName: 'Error',
          status: 'failed',
          timestamp: new Date().toLocaleTimeString(),
          message: err.message || 'Test execution failed',
          isSystem: true
        }
      ]);

      // Set basic error result
      setTestResults({
        passed: 0,
        failed: testSteps.length,
        warnings: 0,
        total: testSteps.length,
        status: 'Failed',
        error: err.message,
        executionDate: new Date().toLocaleDateString(),
        executionTime: new Date().toLocaleTimeString()
      });
    } finally {
      abortControllerRef.current = null;
      setIsCancelling(false);
    }
  };

  const cancelExecution = async () => {
    if (!isExecuting || isCancelling) return;
    setIsCancelling(true);

    try {
      // Tell AI service to cancel the orchestrator
      await axiosInstance.post('/api/test-executions/cancel', {
        control_id: id
      });
    } catch (err) {
      console.error('Failed to send cancel request:', err);
    }

    // Abort the SSE stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setIsExecuting(false);
    setIsCancelling(false);

    // Navigate back to control view
    navigateBackFromRunTest();
  };

  const steps = [
    { number: 1, label: 'Select Evidence' },
    { number: 2, label: 'Map to Steps' },
    { number: 3, label: 'Execute Test' },
    { number: 4, label: 'View Results' }
  ];

  // Loading state - simple spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <div className="text-red-500 text-xl mb-4">Error Loading Control</div>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/sox-controls')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Controls
          </button>
        </div>
      </div>
    );
  }

  // SCRUM-358a/b: resolve the active execution ID so decisions + field edits
  // know where to POST.
  const activeExecutionId = viewExecutionId || latestExecution?.id || null;

  // SCRUM-358b: blur-to-save editor for step result text fields.
  // Click "Edit" to open a textarea; Blur or Enter saves via
  // PATCH /step-field, Esc reverts. Character count surfaces the
  // SCRUM-358d cap in the UI so reviewers see when AI output exceeded
  // the limit (and when their own edits are pushing past it).
  const InlineEditableField = ({ label, initialValue, executionId, stepId, field, maxLength, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(initialValue || '');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const over = maxLength && value.length > maxLength;

    const save = async () => {
      if (!executionId) { setErr('No active execution; cannot save.'); setEditing(false); return; }
      if (value === initialValue) { setEditing(false); return; }
      setSaving(true);
      setErr(null);
      try {
        await axiosInstance.patch(
          `/api/test-executions/${executionId}/step-field`,
          { step_id: String(stepId), field, value }
        );
        if (onSaved) onSaved(value);
        setEditing(false);
      } catch (e) {
        console.error('Edit failed', e);
        setErr(resolve403ErrorMessage(e, e.response?.data?.error || e.message));
      } finally { setSaving(false); }
    };

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">{label}</h3>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs text-blue-600 hover:text-blue-800"
              title="Edit"
              data-testid={`edit-${field}`}
            >Edit</button>
          )}
        </div>
        {editing ? (
          <>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => { if (e.key === 'Escape') { setValue(initialValue || ''); setEditing(false); } }}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800 focus:ring-2 focus:ring-blue-500"
              autoFocus
              disabled={saving}
            />
            <div className="flex items-center justify-between mt-1">
              <span className={`text-xs ${over ? 'text-red-600' : 'text-gray-400'}`}>
                {value.length}{maxLength ? ` / ${maxLength}` : ''} chars
              </span>
              {saving && <span className="text-xs text-gray-400">Saving…</span>}
              {err && <span className="text-xs text-red-600">{err}</span>}
            </div>
          </>
        ) : (
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{value || <span className="text-gray-400">Not set</span>}</p>
        )}
      </div>
    );
  };

  // Modal component for viewing individual step results
  const StepResultModal = ({ step, onClose }) => {
    const result = step?.parsedResult || null;
    const stepId = String(result?.step_number || step?.step || '');
    const [decision, setDecision] = useState(result?.decision || 'draft');
    const [decisionSubmitting, setDecisionSubmitting] = useState(false);
    const [decisionError, setDecisionError] = useState(null);
    if (!step || !result) return null;

    // SCRUM-358a: persist accept/reject/draft decision via PATCH /step-decision
    const setStepDecision = async (newDecision) => {
      setDecisionError(null);
      if (!activeExecutionId) {
        setDecisionError('No active execution — decisions require a loaded run.');
        return;
      }
      setDecisionSubmitting(true);
      try {
        await axiosInstance.patch(
          `/api/test-executions/${activeExecutionId}/step-decision`,
          { step_id: stepId, decision: newDecision }
        );
        setDecision(newDecision);
        result.decision = newDecision;
      } catch (err) {
        console.error('Decision failed', err);
        setDecisionError(resolve403ErrorMessage(err, err.response?.data?.error || err.message || 'Failed to record decision'));
      } finally {
        setDecisionSubmitting(false);
      }
    };

    const getSeverityColor = (severity) => {
      switch (severity) {
        case 'critical': return 'bg-red-100 text-red-800 border-red-200';
        case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
        case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-6 py-4">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-gray-900 whitespace-pre-wrap break-words">Step {step.step}: {step.stepName}</h2>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${result.status === 'passed' ? 'bg-green-100 text-green-800' :
                      result.status === 'failed' ? 'bg-red-100 text-red-800' :
                        result.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                    }`}>
                    {result.status?.toUpperCase()}
                  </span>
                  {/* SCRUM-358a: current decision state + buttons */}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${decision === 'accepted' ? 'bg-green-50 text-green-700 border border-green-200' :
                      decision === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' :
                        'bg-gray-50 text-gray-600 border border-gray-200'
                    }`} data-decision={decision}>
                    Decision: {decision}
                  </span>
                  <button
                    onClick={() => setStepDecision('accepted')}
                    disabled={isLocked || decisionSubmitting || decision === 'accepted'}
                    className={`px-3 py-1 rounded-md text-xs font-medium border ${decision === 'accepted' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-300 hover:bg-green-50'
                      } disabled:opacity-60`}
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => setStepDecision('rejected')}
                    disabled={isLocked || decisionSubmitting || decision === 'rejected'}
                    className={`px-3 py-1 rounded-md text-xs font-medium border ${decision === 'rejected' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-300 hover:bg-red-50'
                      } disabled:opacity-60`}
                  >
                    Reject
                  </button>
                  {decision !== 'draft' && (
                    <button
                      onClick={() => setStepDecision('draft')}
                      disabled={isLocked || decisionSubmitting}
                      className="px-3 py-1 rounded-md text-xs font-medium border bg-white text-gray-600 border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                    >
                      Reset to Draft
                    </button>
                  )}
                </div>
                {decisionError && (
                  <p className="mt-2 text-xs text-red-600" role="alert">
                    {decisionError}
                  </p>
                )}
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto px-6 py-6 space-y-6">
            {/* Sample Details */}
            {(result.population_size || result.sample_size) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Sample Details
                </h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 font-medium">Population</p>
                    <p className="text-gray-900 text-lg font-semibold">{result.population_size || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Sample</p>
                    <p className="text-gray-900 text-lg font-semibold">{result.sample_size || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Method</p>
                    <p className="text-gray-900 text-lg font-semibold capitalize">{result.sampling_method?.replace('_', ' ') || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Findings Summary (SCRUM-358b: inline editable) */}
            {result.findings_summary && (
              <InlineEditableField
                label="Findings Summary"
                initialValue={result.findings_summary}
                executionId={activeExecutionId}
                stepId={stepId}
                field="findings_summary"
                maxLength={280}
                onSaved={(newVal) => { result.findings_summary = newVal; }}
              />
            )}

            {/* Exceptions */}
            {result.exceptions && result.exceptions.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Exceptions Found ({result.exceptions.length})
                </h3>
                <div className="space-y-3">
                  {result.exceptions.map((exc, idx) => (
                    <div key={idx} className={`border rounded-lg p-4 ${getSeverityColor(exc.severity)}`}>
                      <div className="flex items-start gap-3">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold uppercase bg-white bg-opacity-70">
                          {exc.severity || 'Unknown'}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 mb-1">{exc.record_ref}</p>
                          <p className="text-sm">{exc.issue}</p>
                          {exc.amount && <p className="text-sm font-semibold mt-1">Amount: {formatMoney(exc.amount)}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Findings */}
            {result.detailed_findings && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Detailed Analysis</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm">{result.detailed_findings}</p>
              </div>
            )}

            {/* Evidence Analyzed */}
            {result.evidence_analyzed && result.evidence_analyzed.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Evidence Analyzed
                </h3>
                <div className="space-y-2">
                  {result.evidence_analyzed.map((ev, idx) => (
                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="font-medium text-gray-900">{ev.file_name}</p>
                      <div className="flex gap-4 mt-1 text-sm text-gray-600">
                        {ev.tabs_or_pages && <p>Pages: {ev.tabs_or_pages.join(', ')}</p>}
                        {ev.rows_analyzed && <p>Rows: {ev.rows_analyzed}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigateBackFromRunTest()}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Run Test: {controlData.controlId}</h1>
              <p className="text-sm text-gray-500">{controlData.title}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Read-only year lock banner */}
      <div className="max-w-6xl mx-auto px-8 pt-4">
        <ReadOnlyBanner />
      </div>

      {/* Global Error Banner */}
      {saveError && (
        <div className="bg-red-50 border-b border-red-200">
          <div className="max-w-6xl mx-auto px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <p className="text-sm text-red-700">{saveError}</p>
              </div>
              <button
                onClick={() => setSaveError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps - Hide when in view mode */}
      {!isViewMode && (
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                // Determine step state
                // Step 2 loading when analyzing evidence
                // Step 3 loading when executing tests
                const isCompleted = currentStep > step.number ||
                  (isAnalyzing && currentStep === 2 && step.number <= 1) ||
                  (isExecuting && currentStep === 3 && step.number <= 2);
                const isActive = currentStep === step.number;
                const isLoading = (isAnalyzing && currentStep === 2 && step.number === 2) ||
                  (isExecuting && currentStep === 3 && step.number === 3);

                return (
                  <React.Fragment key={step.number}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${isCompleted
                        ? 'bg-green-600 text-white'
                        : isActive
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                        }`}>
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : isLoading ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          step.number
                        )}
                      </div>
                      <span className={`text-sm font-medium ${isCompleted || isActive ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                        {isLoading && step.number === 3 ? 'Analyzing...' :
                          isLoading && step.number === 4 ? 'Executing...' : step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-4 ${isCompleted ? 'bg-green-600' : 'bg-gray-200'
                        }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step Content - relative container for overlay */}
      <div className="max-w-6xl mx-auto px-8 py-8 relative min-h-[calc(100vh-180px)]">
        {/* AI Analysis Progress Overlay - Enhanced version */}
        {isAnalyzing && (
          <div className="absolute inset-0 bg-gray-50 flex items-center justify-center z-30">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-10 w-full max-w-md mx-4">
              <div className="flex flex-col items-center space-y-6">
                {/* Animated Icon with Pulse Effect */}
                <div className="relative">
                  {/* Outer pulse ring */}
                  {analysisProgress < 100 && (
                    <div className="absolute inset-0 rounded-full bg-purple-400 opacity-20 animate-ping"></div>
                  )}

                  {/* Main icon circle */}
                  <div className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-500 ${analysisProgress >= 100
                    ? 'bg-gradient-to-br from-green-400 to-green-600 scale-110'
                    : 'bg-gradient-to-br from-purple-500 to-purple-600'
                    }`}>
                    {/* Spinning loader */}
                    {analysisProgress < 100 && (
                      <Loader2 className="w-10 h-10 text-white animate-spin" />
                    )}

                    {/* Check mark when complete */}
                    {analysisProgress >= 100 && (
                      <Check className="w-10 h-10 text-white" />
                    )}
                  </div>

                  {/* AI Badge */}
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* Title and Description */}
                <div className="text-center space-y-3">
                  <h3 className="text-xl font-bold text-gray-900">AI Evidence Analysis</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Analyzing files and mapping to test steps
                  </p>
                </div>

                {/* Status Message Card */}
                <div className="w-full bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-100">
                  <p className="text-sm text-purple-900 font-medium text-center">
                    {analysisDetail || analysisMessage}
                  </p>
                </div>

                {/* Decorative bouncing dots */}
                {analysisProgress < 100 && (
                  <div className="flex gap-2">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Test History Bar removed - users can view history from ViewControl's Test Runs tab */}

        {/* Step 1: Select Evidence (combined period + file selection) */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Select Evidence</h2>
              <p className="text-sm text-gray-500 mt-1">Choose a testing period and select evidence files for this test</p>
            </div>

            {/* Period Selection — compact dropdown */}
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Testing Period</label>
                <select
                  value={testingPeriodStart === '__evergreen__' ? '__evergreen__'
                    : (testingPeriodStart && testingPeriodEnd && existingPeriods.some(p => (p.testing_period_start?.split('T')[0] || '') === testingPeriodStart && (p.testing_period_end?.split('T')[0] || '') === testingPeriodEnd)) ? `${testingPeriodStart}_${testingPeriodEnd}`
                      : (testingPeriodStart && testingPeriodStart !== '__evergreen__') ? '__new__'
                        : '__evergreen__'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '__evergreen__') {
                      setTestingPeriodStart('__evergreen__');
                      setTestingPeriodEnd('__evergreen__');
                    } else if (val === '__new__') {
                      const dated = existingPeriods.filter(p => p.testing_period_end);
                      let smart = '';
                      if (dated.length > 0) {
                        const lastEnd = dated.reduce((latest, p) => { const d = String(p.testing_period_end).split('T')[0]; return d > latest ? d : latest; }, '');
                        if (lastEnd) { const next = new Date(lastEnd + 'T00:00:00Z'); next.setUTCDate(next.getUTCDate() + 1); smart = next.toISOString().split('T')[0]; }
                      }
                      setTestingPeriodStart(smart || '__new__');
                      setTestingPeriodEnd('');
                    } else if (val) {
                      const [s, en] = val.split('_');
                      setTestingPeriodStart(s);
                      setTestingPeriodEnd(en);
                      const match = existingPeriods.find(p => (p.testing_period_start?.split('T')[0] || '') === s && (p.testing_period_end?.split('T')[0] || '') === en);
                      if (match) setPeriodEvidenceCount(match.file_count || 0);
                    } else {
                      setTestingPeriodStart('__evergreen__');
                      setTestingPeriodEnd('__evergreen__');
                    }
                  }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none min-w-[260px]"
                >
                  <option value="__evergreen__">Evergreen (all periods)</option>
                  {existingPeriods.filter(p => p.testing_period_start && p.testing_period_end).map((p, i) => {
                    const s = p.testing_period_start?.split('T')[0] || '';
                    const en = p.testing_period_end?.split('T')[0] || '';
                    const sp = s.split('-'); const ep = en.split('-');
                    const sDate = new Date(Date.UTC(+sp[0], +sp[1] - 1, +sp[2]));
                    const eDate = new Date(Date.UTC(+ep[0], +ep[1] - 1, +ep[2]));
                    const label = `${sDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })} — ${eDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`;
                    return <option key={i} value={`${s}_${en}`}>{label} ({p.file_count} {p.file_count === 1 ? 'file' : 'files'})</option>;
                  })}
                  <option value="__new__">+ Create new period</option>
                </select>
              </div>
              {testingPeriodStart && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Start</label>
                    <input type="date"
                      value={testingPeriodStart === '__new__' ? '' : testingPeriodStart}
                      onChange={(e) => setTestingPeriodStart(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">End</label>
                    <input type="date" value={testingPeriodEnd} onChange={(e) => setTestingPeriodEnd(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                  </div>
                </>
              )}
              {testingPeriodStart && testingPeriodStart !== '__new__' && testingPeriodEnd && testingPeriodEnd < testingPeriodStart && (
                <span className="text-xs text-red-600 inline-flex items-center gap-1 pb-1">
                  End date must be on or after the start date.
                </span>
              )}
              {testingPeriodStart && testingPeriodStart !== '__new__' && testingPeriodEnd && testingPeriodEnd >= testingPeriodStart && periodEvidenceCount > 0 && (
                <span className="text-xs text-green-600 inline-flex items-center gap-1 pb-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {periodEvidenceCount} evidence {periodEvidenceCount === 1 ? 'file' : 'files'} for this period
                </span>
              )}
            </div>

            {/* Evidence File Selection — only when period selected */}
            {testingPeriodStart && testingPeriodStart !== '__new__' && testingPeriodEnd && testingPeriodEnd >= testingPeriodStart ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">Evidence Files ({periodEvidenceFiles.length})</h3>
                  <div className="flex items-center gap-3">
                    {periodEvidenceFiles.length > 0 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedFiles(periodEvidenceFiles.map(f => f.id))}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => setSelectedFiles([])}
                          className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                        >
                          Deselect All
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center gap-1"
                    >
                      <Upload className="h-3 w-3" />
                      Upload
                    </button>
                  </div>
                </div>
                {periodEvidenceFiles.length === 0 ? (
                  <div className="p-8 text-center">
                    <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-900 mb-1">No evidence for this period</p>
                    <p className="text-xs text-gray-500">Upload evidence files using the button above.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 max-h-[350px] overflow-y-auto">
                    {periodEvidenceFiles.map((file) => (
                      <div
                        key={file.id}
                        className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors ${selectedFiles.includes(file.id) ? 'bg-blue-50' : ''
                          }`}
                        onClick={() => toggleFileSelection(file.id)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.id)}
                          onChange={(e) => { e.stopPropagation(); toggleFileSelection(file.id); }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 cursor-pointer"
                        />
                        <div className="p-1.5 bg-gray-100 rounded">
                          {getFileIcon(file.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                            {/* SCRUM-363: [POP] badge when this file is tagged as a population source */}
                            {(file.evidence_type === 'population_report' || file.role === 'population') && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                POP
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{file.size}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Select a testing period above to see evidence files</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => navigateBackFromRunTest()}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={proceedToMapping}
                disabled={isLocked || selectedFiles.length === 0 || isAnalyzing}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Analyze & Map
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Map Files to Test Steps */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* SCRUM-402 Phase 3: population summary + C&A verification pills.
                Shows only when a population file was supplied and the mapper
                emitted a sampling_context. Amber asks for more inputs when
                verification was inconclusive. */}
            {samplingContext && samplingContext.population_total != null && (() => {
              const renderPill = (label, status) => {
                const tone = status === 'verified'
                  ? 'bg-green-50 text-green-700 border-green-200'
                  : status === 'partial'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : status === 'unverifiable'
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-gray-100 text-gray-500 border-gray-200';
                return (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${tone}`}>
                    {label}: {status || 'not attempted'}
                  </span>
                );
              };
              // SCRUM-403 Phase 4: mode badge indicates whether the AI performed
              // C&A independently or inspected the owner's work.
              const renderModeBadge = (mode) => {
                if (!mode) return null;
                const isInspection = mode === 'inspection_of_control_owner';
                return (
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ml-1 ${isInspection
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200'
                      }`}
                    title={isInspection ? 'AI inspected the control owner\'s C&A work' : 'AI performed C&A independently'}
                  >
                    {isInspection ? '🔍 inspection' : '🤖 independent'}
                  </span>
                );
              };
              const c = samplingContext.completeness;
              const a = samplingContext.accuracy;
              // Mode is control-level — dedupe across both reports and show once.
              const unifiedMode = c?.mode || a?.mode;
              // Deduplicate + trim + truncate individual asks so the list stays scannable.
              const asks = Array.from(new Set([
                ...(c?.ask_auditor_for || []),
                ...(a?.ask_auditor_for || []),
              ].map(s => String(s).trim()).filter(Boolean))).slice(0, 4);
              return (
                <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="font-medium text-gray-800">Population: {samplingContext.population_total}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-purple-50 text-purple-700 border-purple-200">
                      file-derived
                    </span>
                    {unifiedMode && renderModeBadge(unifiedMode)}
                  </div>
                  {(c || a) && (
                    <div className="flex flex-wrap items-center gap-2">
                      {c && renderPill('Completeness', c.status)}
                      {a && renderPill('Accuracy', a.status)}
                    </div>
                  )}
                  {(c?.control_owner_evidence_cited || a?.control_owner_evidence_cited) ? (
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Owner evidence cited: </span>
                      {[c?.control_owner_evidence_cited, a?.control_owner_evidence_cited].filter(Boolean).slice(0, 2).join(' · ')}
                    </div>
                  ) : null}
                  {asks.length > 0 && (
                    <div className="text-xs text-gray-600">
                      <div className="font-medium mb-1">Supporting files needed:</div>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {asks.map((ask, i) => (
                          <li key={i}>{ask.length > 140 ? `${ask.slice(0, 137)}…` : ask}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Confirm AI Evidence Mapping</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {hasExistingMapping
                    ? 'Previously saved mapping loaded. You can modify or re-run the analysis.'
                    : 'Review the AI-suggested evidence mapping below. Adjust if needed, then proceed.'
                  }
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* SCRUM-363: Add more evidence after initial mapping.
                    Re-uses the period picker flow — user uploads or picks
                    a file, then hits Re-run Analysis with the expanded set. */}
                {!isAnalyzing && testMappings.length > 0 && (
                  <label
                    className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 inline-flex items-center gap-2 cursor-pointer"
                    data-testid="add-more-evidence"
                    title="Upload additional evidence to include in mapping"
                  >
                    <Upload className="h-4 w-4" />
                    Add more evidence
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.xlsx,.xls,.csv,.docx"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        // Upload to backend as control evidence so it has a file_id
                        try {
                          const form = new FormData();
                          files.forEach(f => form.append('files', f));
                          await axiosInstance.post(
                            `/api/sox-controls/${id}/data-files`,
                            form,
                            { headers: { 'Content-Type': 'multipart/form-data' } }
                          );
                          // Trigger the existing re-run-analysis path so the mapper
                          // consumes the expanded set. This is the "incremental"
                          // experience for the user even though the server still
                          // analyzes the full union.
                          if (typeof clearMappingAndRerun === 'function') {
                            clearMappingAndRerun();
                          }
                        } catch (err) {
                          console.error('Add more evidence failed:', err);
                          alert('Upload failed: ' + resolve403ErrorMessage(err, err?.response?.data?.error || err.message));
                        }
                      }}
                    />
                  </label>
                )}
                {hasExistingMapping && !isAnalyzing && (
                  <button
                    onClick={clearMappingAndRerun}
                    className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 inline-flex items-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Re-run Analysis
                  </button>
                )}
              </div>
            </div>

            {testSteps.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Test Steps Defined</h3>
                <p className="text-gray-500 mb-4">Add test steps to this control before running a test.</p>
                <button
                  onClick={() => navigateBackFromRunTest()}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Back to Control to Add Test Steps
                </button>
              </div>
            ) : (
              <>
                {/* Mapping Summary Card */}
                {(() => {
                  const mappedCount = testMappings.filter(m => m.files.length > 0).length;
                  const missingCount = testMappings.filter(m => m.files.length === 0).length;
                  const totalCount = testMappings.length;
                  return (
                    <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="text-gray-700"><span className="font-semibold text-gray-900">{mappedCount}</span> of {totalCount} steps mapped</span>
                        </div>
                        {missingCount > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                            <span className="text-red-700 font-medium">{missingCount} missing evidence</span>
                          </div>
                        )}
                      </div>
                      {!analysisError && testMappings.some(m => m.files.some(f => f.tabsOrPages?.length > 0 || f.populationCount || f.sampleCount)) && (
                        <div className="flex items-center gap-1.5 text-xs text-purple-600">
                          <Sparkles className="h-3.5 w-3.5" />
                          <span>AI-analyzed</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Fallback notice - show when AI couldn't run */}
                {analysisError && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">AI Analysis Warning</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        {analysisError}. Showing fallback mappings - you may want to adjust manually.
                      </p>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="font-medium text-gray-900">Test Step Mappings</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {testMappings.map((mapping) => {
                      const isMapped = mapping.files.length > 0;
                      const isExpanded = expandedSteps[mapping.stepIndex] ?? !isMapped;
                      return (
                        <div key={mapping.stepIndex} className={`border-l-4 ${isMapped ? 'border-l-green-400' : 'border-l-red-400'}`}>
                          {/* Clickable toggle header */}
                          <button
                            onClick={() => setExpandedSteps(prev => ({ ...prev, [mapping.stepIndex]: !isExpanded }))}
                            className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left"
                          >
                            {isMapped ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                            )}
                            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-xs">
                              {mapping.stepIndex + 1}
                            </div>
                            <p className="flex-1 text-sm font-medium text-gray-900 truncate">{mapping.stepName}</p>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {isMapped ? (
                                <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded-full font-medium">
                                  {mapping.files.length} file{mapping.files.length !== 1 ? 's' : ''}
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded-full font-medium">
                                  Missing
                                </span>
                              )}
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </button>

                          {/* Collapsible content */}
                          {isExpanded && (
                            <div className="px-6 pb-6 pt-0">
                              <div className="ml-16">
                                {/* Mapped files */}
                                <div className="space-y-3">
                                  {mapping.files.map((file) => (
                                    <div key={file.fileId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          {getFileIcon(file.fileType)}
                                          <span className="font-medium text-sm">{file.fileName}</span>
                                        </div>
                                        <button
                                          onClick={() => removeFileFromMapping(mapping.stepIndex, file.fileId)}
                                          className="p-1 text-gray-400 hover:text-red-600 rounded"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>

                                      {/* AI-identified tabs/pages */}
                                      {file.tabsOrPages && file.tabsOrPages.length > 0 && (
                                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                                          <Layers className="h-3.5 w-3.5 text-purple-500" />
                                          {file.tabsOrPages.map((tab, idx) => (
                                            <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                              {tab}
                                            </span>
                                          ))}
                                        </div>
                                      )}

                                      {/* Per-source sampling info (from multi-stage analysis) */}
                                      {(file.populationCount || file.sampleCount) && (
                                        <div className="mt-2 text-xs text-gray-600 flex items-center gap-3 flex-wrap">
                                          {file.populationCount && (
                                            <span>Pop: <span className="font-medium text-gray-900">{file.populationCount}</span></span>
                                          )}
                                          {file.sampleCount && (
                                            <span>Sample: <span className="font-medium text-gray-900">{file.sampleCount}</span></span>
                                          )}
                                          {file.samplingMethod && (
                                            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-700 capitalize">
                                              {file.samplingMethod.replace('_', ' ')}
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {/* Data summary from chunk analysis */}
                                      {file.dataSummary && (
                                        <p className="text-xs text-gray-500 mt-1">{file.dataSummary}</p>
                                      )}

                                      {/* Preparer/Reviewer info */}
                                      {(file.preparerInfo || file.reviewerInfo) && (
                                        <div className="mt-2 flex gap-2 flex-wrap">
                                          {file.preparerInfo && (
                                            <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                                              Preparer: {file.preparerInfo}
                                            </span>
                                          )}
                                          {file.reviewerInfo && (
                                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                                              Reviewer: {file.reviewerInfo}
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {/* AI usage summary */}
                                      {file.usageSummary ? (
                                        <p className="text-xs text-gray-600 mt-2 italic">{file.usageSummary}</p>
                                      ) : (
                                        <p className="text-xs text-gray-500 mt-2">File will be analyzed for this test step</p>
                                      )}

                                      {/* NEW: User notes */}
                                      {file.userNotes && (
                                        <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded">
                                          <p className="text-xs font-medium text-blue-700">Your Notes:</p>
                                          <p className="text-xs text-blue-900 mt-1">{file.userNotes}</p>
                                        </div>
                                      )}

                                      {/* NEW: Edit notes button */}
                                      <button
                                        onClick={() => openEditNotesModal(mapping.stepIndex, file)}
                                        className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline"
                                      >
                                        {file.userNotes ? 'Edit Notes' : 'Add Notes'}
                                      </button>
                                    </div>
                                  ))}

                                  {mapping.files.length === 0 && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                      <div className="flex items-start gap-3">
                                        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-red-800">No Evidence Mapped</p>

                                          {/* AI explanation - always show by default */}
                                          {mapping.unmappedExplanation ? (
                                            <p className="text-sm text-red-700 mt-1">{mapping.unmappedExplanation}</p>
                                          ) : mapping.isLoadingExplanation ? (
                                            <p className="text-sm text-red-600 mt-1 flex items-center gap-2">
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                              Analyzing why no files matched...
                                            </p>
                                          ) : (
                                            <p className="text-sm text-red-700 mt-1 italic">
                                              No evidence files match the requirements for this test step.
                                            </p>
                                          )}

                                          {/* Suggestions */}
                                          {mapping.unmappedSuggestions?.length > 0 && (
                                            <div className="mt-2">
                                              <p className="text-xs text-red-700 font-medium">Suggested evidence:</p>
                                              <ul className="text-xs text-red-600 list-disc list-inside mt-1">
                                                {mapping.unmappedSuggestions.map((s, i) => (
                                                  <li key={i}>{s}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Sampling Info (if detected by AI) */}
                                {mapping.samplingInfo && (
                                  <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Info className="h-4 w-4 text-blue-600" />
                                      <span className="text-xs font-medium text-blue-800">Detected Sampling Strategy</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                      {mapping.samplingInfo.sampling_method && (
                                        <div>
                                          <span className="text-gray-500">Method:</span>
                                          <span className="ml-1 font-medium text-gray-900 capitalize">
                                            {mapping.samplingInfo.sampling_method.replace('_', ' ')}
                                          </span>
                                        </div>
                                      )}
                                      {mapping.samplingInfo.sample_count != null && (
                                        <div>
                                          <span className="text-gray-500">Sample:</span>
                                          <span className="ml-1 font-medium text-gray-900">{mapping.samplingInfo.sample_count}</span>
                                        </div>
                                      )}
                                      {mapping.samplingInfo.population_count != null && (
                                        <div>
                                          <span className="text-gray-500">Population:</span>
                                          <span className="ml-1 font-medium text-gray-900">{mapping.samplingInfo.population_count}</span>
                                        </div>
                                      )}
                                      {mapping.samplingInfo.sampling_criteria && (
                                        <div className="col-span-2">
                                          <span className="text-gray-500">Criteria:</span>
                                          <span className="ml-1 font-medium text-gray-900">{mapping.samplingInfo.sampling_criteria}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                <button
                                  onClick={() => {
                                    setCurrentMappingStep(mapping.stepIndex);
                                    setShowAddFileModal(true);
                                  }}
                                  className="mt-3 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                                >
                                  <Plus className="h-4 w-4" />
                                  Add File
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep(1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <button
                onClick={() => startExecution()}
                disabled={isLocked}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className="h-4 w-4" />
                Run Test
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Execute Test */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Header with Running Badge */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Test Execution</h2>
                <p className="text-sm text-gray-500 mt-1">
                  AI is analyzing evidence files in parallel
                </p>
              </div>
              {!testResults && (() => {
                const inProgressCount = executionLogs.filter(l => !l.isSystem && l.visible !== false && l.status === 'in-progress' && !l.resultStatus).length;
                return (
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-full inline-flex items-center gap-2">
                      <Sparkles className="h-3 w-3 animate-pulse" />
                      {inProgressCount > 1 ? `${inProgressCount} Agents Running...` : 'AI Running...'}
                    </span>
                    <button
                      onClick={cancelExecution}
                      disabled={isCancelling}
                      className="px-3 py-1 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-full inline-flex items-center gap-1 hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      <XCircle className="h-3 w-3" />
                      {isCancelling ? 'Cancelling...' : 'Cancel Test'}
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Progress Overview Card - Shows parallel execution status */}
            {!testResults && executionLogs.length > 0 && (() => {
              // Calculate parallel execution stats
              const stepLogs = executionLogs.filter(l => !l.isSystem && l.visible !== false);
              const inProgressSteps = stepLogs.filter(l => l.status === 'in-progress' && !l.resultStatus);
              const completedSteps = stepLogs.filter(l => l.resultStatus && ['passed', 'failed', 'warning'].includes(l.resultStatus));
              const totalSteps = testSteps.length;
              const completedPercent = totalSteps > 0 ? Math.round((completedSteps.length / totalSteps) * 100) : 0;

              return (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                      <Sparkles className="h-6 w-6 text-purple-600 animate-pulse" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {inProgressSteps.length > 0
                          ? 'Analyzing Test Steps'
                          : executionLogs.find(l => l.isSystem)?.stepName || 'Processing...'}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {inProgressSteps.length > 0
                          ? `Running: ${inProgressSteps.map(l => `Step ${l.step}`).join(', ')}`
                          : executionLogs.find(l => l.isSystem)?.message || 'Initializing test execution...'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-purple-600">
                        {completedSteps.length}/{totalSteps}
                      </p>
                      <p className="text-xs text-gray-500">steps done</p>
                    </div>
                  </div>
                  {/* Progress bar based on completed steps */}
                  <div className="mt-4 h-2 bg-white rounded-full overflow-hidden">
                    <div
                      className="h-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${completedPercent}%` }}
                    />
                  </div>
                  {/* Status summary badges */}
                  {completedSteps.length > 0 && (
                    <div className="mt-3 flex items-center gap-3 text-xs">
                      {completedSteps.filter(l => l.resultStatus === 'passed').length > 0 && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                          ✓ {completedSteps.filter(l => l.resultStatus === 'passed').length} passed
                        </span>
                      )}
                      {completedSteps.filter(l => l.resultStatus === 'failed').length > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full">
                          ✗ {completedSteps.filter(l => l.resultStatus === 'failed').length} failed
                        </span>
                      )}
                      {completedSteps.filter(l => l.resultStatus === 'warning').length > 0 && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                          ⚠ {completedSteps.filter(l => l.resultStatus === 'warning').length} warnings
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Execution Log Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-900">Execution Progress</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {executionLogs.filter(l => !l.isSystem && l.visible !== false).map((log, index) => {
                  // Runtime stream is read-only: no per-step interactions while the run is live.
                  // Accept/Reject + field edits happen from the post-run Summary/Step N tabs.
                  const isClickable = !isExecuting && (log.parsedResult || log.stepResult);
                  const isComplete = log.resultStatus && ['passed', 'failed', 'warning'].includes(log.resultStatus);

                  return (
                    <div
                      key={index}
                      className={`p-4 transition-colors ${isClickable
                        ? 'cursor-pointer hover:bg-blue-50 hover:border-l-4 hover:border-blue-500'
                        : ''
                        }`}
                      onClick={() => {
                        if (isClickable) {
                          setSelectedStepModal(log);
                        }
                      }}
                    >
                      <div className="flex items-start gap-4">
                        {/* Status Icon */}
                        <div className="flex-shrink-0 mt-0.5">
                          {log.status === 'in-progress' && !isComplete && (
                            <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                          )}
                          {(log.status === 'completed' || log.resultStatus === 'passed') && (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          )}
                          {(log.status === 'failed' || log.resultStatus === 'failed') && (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          {(log.status === 'warning' || log.resultStatus === 'warning') && (
                            <AlertCircle className="w-5 h-5 text-yellow-600" />
                          )}
                        </div>
                        {/* Log Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-gray-900">
                              Step {log.step}
                            </p>
                            {/* Status Badge */}
                            {log.resultStatus === 'passed' && (
                              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded flex-shrink-0">
                                Passed
                              </span>
                            )}
                            {log.resultStatus === 'failed' && (
                              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded flex-shrink-0">
                                Failed
                              </span>
                            )}
                            {log.resultStatus === 'warning' && (
                              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded flex-shrink-0">
                                Warning
                              </span>
                            )}
                            {log.status === 'in-progress' && !log.resultStatus && (
                              <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded flex-shrink-0">
                                Analyzing...
                              </span>
                            )}
                          </div>
                          {/* Show step description - always show if available and no findings yet */}
                          {!log.findings && log.stepName && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                              {typeof log.stepName === 'string' ? log.stepName.substring(0, 80) : ''}...
                            </p>
                          )}
                          {/* Findings summary - show when we have results */}
                          {log.findings && (
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {log.findings}
                            </p>
                          )}
                          {/* Exception count */}
                          {log.exceptions > 0 && (
                            <p className="text-xs text-red-600 mt-1">
                              {log.exceptions} exception{log.exceptions > 1 ? 's' : ''} found
                            </p>
                          )}
                          {log.timestamp && (
                            <p className="text-xs text-gray-400 mt-1">{log.timestamp}</p>
                          )}
                        </div>
                      </div>
                      {/* Click hint for completed steps */}
                      {isClickable && (
                        <div className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Click to view details
                        </div>
                      )}
                    </div>
                  );
                })}
                {executionLogs.filter(l => !l.isSystem && l.visible !== false).length === 0 && !testResults && (
                  <div className="p-8 text-center text-gray-500">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-500" />
                    <p>Initializing AI test agents...</p>
                    <p className="text-xs mt-2 text-gray-400">This may take a few minutes depending on file size</p>
                  </div>
                )}
              </div>
            </div>

            {/* View Results Button */}
            {testResults && (
              <div className="flex justify-center pt-6">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-3 text-base font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
                >
                  View Results
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 5: View Results */}
        {currentStep === 4 && testResults && (
          <div className="space-y-6">
            {/* View Mode Header - Simple info banner when viewing saved results */}
            {isViewMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <span className="text-sm text-blue-700">
                  Viewing saved results from Test Run #{testResults.test_run_number || 1}
                  {testResults.executionDate && ` - ${testResults.executionDate}`}
                </span>
              </div>
            )}

            {/* Success/Failure Banner */}
            {testResults.status === 'Passed' ? (
              <div className="p-6 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-900">Test Completed Successfully</p>
                    <p className="text-sm text-green-700">All test steps passed validation</p>
                  </div>
                </div>
              </div>
            ) : testResults.status === 'Failed' ? (
              <div className="p-6 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-900">Test Failed</p>
                    <p className="text-sm text-red-700">{testResults.failed} step(s) require attention • {testResults.totalExceptions || 0} exception(s) found</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                  <div>
                    <p className="font-semibold text-yellow-900">Test Completed with Warnings</p>
                    <p className="text-sm text-yellow-700">{testResults.warnings} warning(s) to review</p>
                  </div>
                </div>
              </div>
            )}

            {/* Results Summary Grid */}
            <div className="grid grid-cols-4 gap-4">
              <div className="p-5 text-center bg-green-50 border border-green-200 rounded-xl">
                <p className="text-3xl font-bold text-green-600 mb-1">{testResults.passed}</p>
                <p className="text-sm text-gray-600">Passed</p>
              </div>
              <div className="p-5 text-center bg-red-50 border border-red-200 rounded-xl">
                <p className="text-3xl font-bold text-red-600 mb-1">{testResults.failed}</p>
                <p className="text-sm text-gray-600">Failed</p>
              </div>
              <div className="p-5 text-center bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-3xl font-bold text-yellow-600 mb-1">{testResults.warnings}</p>
                <p className="text-sm text-gray-600">Warnings</p>
              </div>
              <div className="p-5 text-center bg-purple-50 border border-purple-200 rounded-xl">
                <p className="text-3xl font-bold text-purple-600 mb-1">{testResults.totalExceptions || 0}</p>
                <p className="text-sm text-gray-600">Exceptions</p>
              </div>
            </div>

            {/* Results Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Tab Headers */}
              <div className="border-b border-gray-200 bg-gray-50">
                <div className="flex overflow-x-auto">
                  <button
                    onClick={() => setActiveResultTab('walkthrough')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeResultTab === 'walkthrough'
                      ? 'border-blue-600 text-blue-600 bg-white'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <ClipboardList className="h-4 w-4 inline mr-2" />
                    Summary
                  </button>
                  {(testResults.stepResults || []).map((sr, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveResultTab(`step-${sr.step_number || idx + 1}`)}
                      className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${activeResultTab === `step-${sr.step_number || idx + 1}`
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      {sr.status === 'passed' && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                      {sr.status === 'failed' && <XCircle className="h-3.5 w-3.5 text-red-500" />}
                      {sr.status === 'warning' && <AlertCircle className="h-3.5 w-3.5 text-yellow-500" />}
                      Step {sr.step_number || idx + 1}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Walkthrough Tab - Executive Summary */}
                {activeResultTab === 'walkthrough' && testResults.walkthrough && (
                  <div className="space-y-6">
                    {/* Confidence Score */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-gray-400" />
                        <span className="text-sm text-gray-500">Confidence:</span>
                      </div>
                      <div className="flex-1 max-w-xs">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-2 rounded-full ${testResults.walkthrough.confidence_score >= 0.8 ? 'bg-green-500' :
                              testResults.walkthrough.confidence_score >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                            style={{ width: `${(testResults.walkthrough.confidence_score || 0) * 100}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {Math.round((testResults.walkthrough.confidence_score || 0) * 100)}%
                      </span>
                    </div>

                    {/* Executive Summary */}
                    <div className="bg-gray-50 rounded-lg p-5">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Info className="h-4 w-4 text-blue-500" />
                        Executive Summary
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                        {testResults.walkthrough.executive_summary || 'No summary available'}
                      </p>
                    </div>

                    {/* Key Findings */}
                    {testResults.walkthrough.key_findings?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <Flag className="h-4 w-4 text-purple-500" />
                          Key Findings
                        </h4>
                        <ul className="space-y-2">
                          {testResults.walkthrough.key_findings.map((finding, idx) => (
                            <li key={idx} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-3">
                              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">
                                {idx + 1}
                              </span>
                              <span className="text-sm text-gray-700">{finding}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Recommendations */}
                    {testResults.walkthrough.recommendations?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-amber-500" />
                          Recommendations
                        </h4>
                        <ul className="space-y-2">
                          {testResults.walkthrough.recommendations.map((rec, idx) => (
                            <li key={idx} className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-700">{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Test Metadata */}
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 mb-1">Control ID</p>
                          <p className="font-medium text-gray-900">{testResults.controlId || controlData.controlId}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-1">Test Date</p>
                          <p className="font-medium text-gray-900">{testResults.executionDate}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-1">Duration</p>
                          <p className="font-medium text-gray-900">
                            {formatDuration(testResults.executionDuration)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-1">Files Analyzed</p>
                          <p className="font-medium text-gray-900">{testResults.filesAnalyzed?.length || 0}</p>
                        </div>
                      </div>
                    </div>

                    {/* Sample Results Summary Table */}
                    {testResults.stepResults?.some(sr => sr.sample_results?.length > 0) && (
                      <div className="mt-6">
                        <h4 className="font-medium text-gray-900 mb-3">Sample Results by Step</h4>
                        <div className="overflow-hidden border border-gray-200 rounded-lg">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="px-4 py-2 text-left font-medium text-gray-700 border-b border-gray-200">Step</th>
                                <th className="px-4 py-2 text-center font-medium text-green-700 border-b border-gray-200">Pass</th>
                                <th className="px-4 py-2 text-center font-medium text-red-700 border-b border-gray-200">Fail</th>
                                <th className="px-4 py-2 text-center font-medium text-gray-700 border-b border-gray-200">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {testResults.stepResults.map((sr, idx) => (
                                <tr key={idx} className="border-b border-gray-100">
                                  <td className="px-4 py-2 text-gray-900">Step {sr.step_number || idx + 1}</td>
                                  <td className="px-4 py-2 text-center text-green-600 font-medium">{sr.samples_passed || 0}</td>
                                  <td className="px-4 py-2 text-center text-red-600 font-medium">{sr.samples_failed || 0}</td>
                                  <td className="px-4 py-2 text-center font-medium">{sr.sample_results?.length || 0}</td>
                                </tr>
                              ))}
                              <tr className="bg-gray-50 font-bold">
                                <td className="px-4 py-2 text-gray-900">Total</td>
                                <td className="px-4 py-2 text-center text-green-600">
                                  {testResults.stepResults.reduce((sum, sr) => sum + (sr.samples_passed || 0), 0)}
                                </td>
                                <td className="px-4 py-2 text-center text-red-600">
                                  {testResults.stepResults.reduce((sum, sr) => sum + (sr.samples_failed || 0), 0)}
                                </td>
                                <td className="px-4 py-2 text-center">
                                  {testResults.stepResults.reduce((sum, sr) => sum + (sr.sample_results?.length || 0), 0)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Cross-Step Sample Detail Matrix */}
                    {testResults.stepResults?.some(sr => sr.sample_results?.length > 0) && (
                      <div className="mt-6">
                        <h4 className="font-medium text-gray-900 mb-3">Sample Detail Matrix</h4>
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Sample</th>
                                {testResults.stepResults.map((sr, idx) => (
                                  <th key={idx} className="px-3 py-2 text-center font-medium text-gray-600">
                                    Step {sr.step_number || idx + 1}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                // Normalize record_ref to handle inconsistent AI formatting
                                // e.g., "PR-291498", "PR #291498", "PR#291498" all become "PR #291498"
                                const normalizeRef = (ref) => {
                                  return ref
                                    .replace(/[-_]/g, ' ')           // "PR-291498" → "PR 291498"
                                    .replace(/\s*#\s*/g, ' #')       // "PR#291498" → "PR #291498"
                                    .replace(/([A-Za-z]+)\s+(\d)/g, '$1 #$2')  // "PR 291498" → "PR #291498"
                                    .replace(/\s+/g, ' ')            // collapse multiple spaces
                                    .trim();
                                };

                                // Build normalized lookup: normalizedRef → { display, steps: { stepIdx: status } }
                                const refMap = {};
                                testResults.stepResults.forEach((sr, sIdx) => {
                                  (sr.sample_results || []).forEach(s => {
                                    const normalized = normalizeRef(s.record_ref);
                                    if (!refMap[normalized]) {
                                      refMap[normalized] = { display: s.record_ref, steps: {} };
                                    }
                                    refMap[normalized].steps[sIdx] = s.status;
                                  });
                                });

                                const uniqueRefs = Object.keys(refMap);

                                return uniqueRefs.map((normRef, rIdx) => (
                                  <tr key={rIdx} className="border-b border-gray-100">
                                    <td className="px-3 py-2 font-medium text-gray-900">{refMap[normRef].display}</td>
                                    {testResults.stepResults.map((_, sIdx) => (
                                      <td key={sIdx} className="px-3 py-2 text-center">
                                        {refMap[normRef].steps[sIdx] === 'passed' ? (
                                          <span className="text-green-600 font-medium">Pass</span>
                                        ) : refMap[normRef].steps[sIdx] === 'failed' ? (
                                          <span className="text-red-600 font-medium">Fail</span>
                                        ) : (
                                          <span className="text-gray-400">—</span>
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ));
                              })()}
                              <tr className="bg-gray-50 font-bold border-t border-gray-200">
                                <td className="px-3 py-2">Total</td>
                                {testResults.stepResults.map((sr, idx) => (
                                  <td key={idx} className="px-3 py-2 text-center">
                                    <span className="text-green-600">{sr.samples_passed || 0}</span>
                                    {' / '}
                                    <span className="text-red-600">{sr.samples_failed || 0}</span>
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Walkthrough Tab - Fallback if no walkthrough data */}
                {activeResultTab === 'walkthrough' && !testResults.walkthrough && (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No walkthrough summary available</p>
                    {testResults.error && (
                      <p className="text-red-500 text-sm mt-2">{testResults.error}</p>
                    )}
                  </div>
                )}

                {/* Step Tabs - Per-Step Detailed Results */}
                {(testResults.stepResults || []).map((sr, idx) => {
                  const stepNum = sr.step_number || idx + 1;
                  if (activeResultTab !== `step-${stepNum}`) return null;

                  return (
                    <div key={idx} className="space-y-5">
                      {/* Step Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 whitespace-pre-wrap break-words">
                            Step {stepNum}: {sr.step_description || testSteps[idx] || 'Test Step'}
                          </h4>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${sr.status === 'passed' ? 'bg-green-100 text-green-700' :
                              sr.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                              {sr.status?.toUpperCase() || 'UNKNOWN'}
                            </span>
                            <span className="text-sm text-gray-500">
                              Confidence: {Math.round((sr.confidence_score || 0) * 100)}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Population & Sample Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-600 mb-1">Population</p>
                          <p className="text-lg font-bold text-blue-900">{sr.population_size ?? '—'}</p>
                          {/* SCRUM-363 Phase 1: provenance pill — file-derived /
                              ai-inferred / none. Auditor-facing indicator of
                              whether this number came from a counted population
                              file or an LLM guess from evidence. */}
                          {sr.population_source && (
                            <span
                              className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${sr.population_source === 'population_file'
                                ? 'bg-purple-50 text-purple-700 border-purple-200'
                                : sr.population_source === 'ai_inferred'
                                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                                  : 'bg-gray-100 text-gray-500 border-gray-200'
                                }`}
                              title={
                                sr.population_source === 'population_file'
                                  ? 'Counted deterministically from the uploaded population file'
                                  : sr.population_source === 'ai_inferred'
                                    ? 'AI inferred from evidence — no population file provided'
                                    : 'No population data available for this step'
                              }
                            >
                              {sr.population_source === 'population_file' ? 'file-derived'
                                : sr.population_source === 'ai_inferred' ? 'ai-inferred'
                                  : 'none'}
                            </span>
                          )}
                        </div>
                        <div className="bg-indigo-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-indigo-600 mb-1">Sample</p>
                          <p className="text-lg font-bold text-indigo-900">{sr.sample_size ?? '—'}</p>
                          {/* SCRUM-401 Phase 2: AICPA recommended-range pill.
                              Green when within range, amber below (justification required),
                              neutral above (more coverage is fine). */}
                          {(sr.recommended_sample_size_min != null && sr.recommended_sample_size_max != null) && (() => {
                            const s = sr.sample_size;
                            const lo = sr.recommended_sample_size_min;
                            const hi = sr.recommended_sample_size_max;
                            let tone = 'bg-gray-100 text-gray-600 border-gray-200';
                            let label = `recommended ${lo}–${hi}`;
                            if (s != null && s >= lo && s <= hi) {
                              tone = 'bg-green-50 text-green-700 border-green-200';
                              label = `✓ within ${lo}–${hi}`;
                            } else if (s != null && s < lo) {
                              tone = 'bg-amber-50 text-amber-700 border-amber-200';
                              label = `⚠ below min ${lo}`;
                            } else if (s != null && s > hi) {
                              tone = 'bg-gray-100 text-gray-600 border-gray-200';
                              label = `above max ${hi}`;
                            }
                            return (
                              <span
                                className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${tone}`}
                                title={sr.recommended_rationale || 'AICPA attribute-sampling guidance'}
                              >{label}</span>
                            );
                          })()}
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-purple-600 mb-1">Exceptions</p>
                          <p className="text-lg font-bold text-purple-900">{sr.exceptions_found ?? 0}</p>
                        </div>
                        <div className={`rounded-lg p-3 text-center ${sr.segregation_verified ? 'bg-green-50' : 'bg-gray-50'}`}>
                          <p className="text-xs text-gray-600 mb-1">Segregation</p>
                          <p className={`text-sm font-medium ${sr.segregation_verified ? 'text-green-700' : 'text-gray-500'}`}>
                            {sr.segregation_verified ? '✓ Verified' : 'Not Verified'}
                          </p>
                        </div>
                      </div>

                      {/* SCRUM-404 Phase 5: two-tiered sampling display. Shows tier-1
                          period selection + tier-2 items when the control is two-tiered. */}
                      {sr.tiered_sample && (
                        <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Two-tiered sampling</div>
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="font-medium text-gray-800">Tier 1 — periods:</span>
                            <span className="text-gray-700">
                              {(sr.tiered_sample.tier_1_periods_sampled || []).join(', ') || '—'}
                            </span>
                            {sr.tiered_sample.tier_1_recommended_min != null && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-teal-50 text-teal-700 border-teal-200">
                                recommended {sr.tiered_sample.tier_1_recommended_min}–{sr.tiered_sample.tier_1_recommended_max} of {sr.tiered_sample.tier_1_population_size}
                              </span>
                            )}
                          </div>
                          {sr.tiered_sample.criterion && (
                            <div className="text-xs text-gray-600">
                              <span className="font-medium">Tier-2 criterion: </span>
                              {sr.tiered_sample.criterion}
                            </div>
                          )}
                          {sr.tiered_sample.tier_2_population_size != null && (
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="font-medium text-gray-800">Tier 2 — population:</span>
                              <span className="text-gray-700">{sr.tiered_sample.tier_2_population_size}</span>
                              {sr.tiered_sample.tier_2_recommended_min != null && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-teal-50 text-teal-700 border-teal-200">
                                  recommended {sr.tiered_sample.tier_2_recommended_min}–{sr.tiered_sample.tier_2_recommended_max}
                                </span>
                              )}
                            </div>
                          )}
                          {sr.tiered_sample.tier_2_items_sampled?.length > 0 && (
                            <div className="text-xs text-gray-600">
                              <span className="font-medium">Tier-2 items: </span>
                              {sr.tiered_sample.tier_2_items_sampled.join(', ')}
                            </div>
                          )}
                        </div>
                      )}

                      {/* SCRUM-401 Phase 2: justification textbox surfaces only when
                          actual sample is below the AICPA recommended minimum. Writes
                          to step_results[n].sample_size_justification via the existing
                          PATCH /step-field endpoint (whitelist updated in step_edit_handlers.go). */}
                      {sr.recommended_sample_size_min != null
                        && sr.sample_size != null
                        && sr.sample_size < sr.recommended_sample_size_min && (
                          <InlineEditableField
                            label="Auditor comment"
                            initialValue={sr.sample_size_justification || ''}
                            executionId={activeExecutionId}
                            stepId={stepNum}
                            field="sample_size_justification"
                            maxLength={500}
                            onSaved={(v) => {
                              sr.sample_size_justification = v;
                            }}
                          />
                        )}

                      {/* Preparer/Reviewer */}
                      {(sr.preparer_info || sr.reviewer_info) && (
                        <div className="flex gap-4">
                          {sr.preparer_info && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                              <Users className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">Preparer: <span className="font-medium text-gray-900">{sr.preparer_info}</span></span>
                            </div>
                          )}
                          {sr.reviewer_info && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                              <Users className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">Reviewer: <span className="font-medium text-gray-900">{sr.reviewer_info}</span></span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Procedures performed */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-gray-900 mb-2">Procedures performed</h5>
                        <p className="text-sm text-gray-700">{sr.findings_summary || 'No procedures summary available'}</p>
                      </div>

                      {/* Observations */}
                      {sr.detailed_findings && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Observations</h5>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{sr.detailed_findings}</p>
                        </div>
                      )}

                      {/* Evidence Annotations - deduplicated, one per unique file per step */}
                      <EvidenceAnnotationsSection stepResult={sr} control={control} evidenceFiles={periodEvidenceFiles} />

                      {/* Evidence Sources removed — consolidated into Evidence Annotations section above */}

                      {/* Exceptions noted */}
                      {(!sr.exceptions || sr.exceptions.length === 0) && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Exceptions noted</h5>
                          <p className="text-sm text-gray-500 italic">None</p>
                        </div>
                      )}
                      {sr.exceptions?.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                            <FileWarning className="h-4 w-4 text-red-500" />
                            Exceptions noted ({sr.exceptions.length})
                          </h5>
                          <div className="space-y-2">
                            {sr.exceptions.map((exc, excIdx) => (
                              <div key={excIdx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-2">
                                    <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${exc.severity === 'critical' ? 'bg-red-600 text-white' :
                                      exc.severity === 'high' ? 'bg-red-500 text-white' :
                                        exc.severity === 'medium' ? 'bg-orange-500 text-white' :
                                          'bg-yellow-500 text-white'
                                      }`}>
                                      {exc.severity?.toUpperCase() || 'MEDIUM'}
                                    </span>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{exc.record_ref || 'No reference'}</p>
                                      <p className="text-sm text-gray-600 mt-0.5">{exc.issue || 'No description'}</p>
                                      {/* Citation badge removed — evidence shown in Evidence Annotations section */}
                                    </div>
                                  </div>
                                  {exc.amount && (
                                    <span className="text-sm font-mono text-red-700">{formatMoney(exc.amount)}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Testing details - Sample Results Table with Dynamic Attribute Columns
                          Spec: specs/30-modules/skeptics-frontend/run-test-flow.spec.md
                          - Declared mode: use control step's test_attributes in declared order
                          - Legacy mode: use union of observed keys across samples
                      */}
                      {sr.sample_results?.length > 0 && (() => {
                        const matchedControlStep = (control?.test_steps || [])[idx] || {};
                        const declared = matchedControlStep.test_attributes || [];

                        let columns;
                        if (declared.length > 0) {
                          // Declared mode: fixed columns from contract, in declared order
                          columns = declared.map(a => ({
                            key: a.key,
                            label: a.label || a.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                            description: a.description,
                            data_type: a.data_type,
                            expected_value: a.expected_value,
                            required: a.required !== false,
                            mode: 'declared',
                          }));
                        } else {
                          // Legacy mode: union of observed keys across samples
                          const observed = new Set();
                          for (const s of sr.sample_results) {
                            for (const k of Object.keys(s.attributes || {})) observed.add(k);
                          }
                          columns = Array.from(observed).map(k => ({
                            key: k,
                            label: k,
                            data_type: 'text',
                            expected_value: null,
                            required: false,
                            mode: 'legacy',
                          }));
                        }
                        const hasAttributes = columns.length > 0;

                        const renderCell = (col, sample) => {
                          const raw = sample.attributes?.[col.key];
                          const missing = raw === null || raw === undefined || raw === '';
                          if (missing) {
                            const cls = col.required
                              ? 'px-3 py-2 text-gray-400 border border-dashed border-red-300 rounded-sm'
                              : 'px-3 py-2 text-gray-400';
                            return <td key={col.key} className={cls}>—</td>;
                          }
                          // Boolean cell styling
                          if (col.data_type === 'boolean') {
                            const truthy = String(raw).toLowerCase() === 'true';
                            const cls = col.expected_value && String(raw).toLowerCase() === String(col.expected_value).toLowerCase()
                              ? 'px-3 py-2 bg-green-100 text-green-800'
                              : col.expected_value
                                ? 'px-3 py-2 bg-red-100 text-red-800'
                                : 'px-3 py-2 text-gray-700';
                            return <td key={col.key} className={cls}>{truthy ? '✓ true' : '✗ false'}</td>;
                          }
                          return <td key={col.key} className="px-3 py-2 text-gray-700">{String(raw)}</td>;
                        };

                        return (
                          <div>
                            <h5 className="text-sm font-medium text-gray-900 mb-3">
                              Testing details ({sr.samples_passed || 0} passed, {sr.samples_failed || 0} failed)
                            </h5>
                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="px-3 py-2 text-left font-medium text-gray-600 w-12">#</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-600">Record Ref</th>
                                    {hasAttributes ? (
                                      columns.map(col => (
                                        <th key={col.key} className="px-3 py-2 text-left font-medium text-gray-600" title={col.description || col.label}>
                                          {col.label}
                                        </th>
                                      ))
                                    ) : (
                                      <th className="px-3 py-2 text-left font-medium text-gray-600">Observation</th>
                                    )}
                                    <th className="px-3 py-2 text-center font-medium text-gray-600 w-24">Pass/Fail</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sr.sample_results.map((sample, sIdx) => (
                                    <tr key={sIdx} className={`border-b border-gray-100 ${sample.status === 'passed' ? 'bg-green-50/50' : 'bg-red-50/50'
                                      }`}>
                                      <td className="px-3 py-2 text-gray-500">{sIdx + 1}</td>
                                      <td className="px-3 py-2 font-medium text-gray-900">{sample.record_ref}</td>
                                      {hasAttributes ? (
                                        columns.map(col => renderCell(col, sample))
                                      ) : (
                                        <td className="px-3 py-2 text-gray-700">{sample.observation || '—'}</td>
                                      )}
                                      <td className="px-3 py-2 text-center">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sample.status === 'passed'
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-red-100 text-red-700'
                                          }`}>
                                          {sample.status === 'passed' ? 'Pass' : 'Fail'}
                                        </span>
                                      </td>
                                      {/* Source column removed — evidence shown in Evidence Annotations section */}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Red Flags */}
                      {sr.red_flags?.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Flag className="h-4 w-4 text-amber-500" />
                            Red Flags
                          </h5>
                          <ul className="space-y-1">
                            {sr.red_flags.map((flag, flagIdx) => (
                              <li key={flagIdx} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded">
                                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                                {flag}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Download Buttons */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Download Full Results</p>
              <div className="flex gap-3">
                <button
                  onClick={downloadResultsAsExcel}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 inline-flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Excel (.xlsx)
                </button>
                <button
                  onClick={downloadResultsAsPdf}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 inline-flex items-center justify-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  PDF (.pdf)
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end">
              {!isViewMode && (
                <>
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="mr-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Mapping
                  </button>
                  <button
                    onClick={() => setShowContextModal(true)}
                    className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center gap-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Re-run with Context
                  </button>
                  <button
                    onClick={() => {
                      setCurrentStep(1);
                      setSelectedFiles([]);
                      setTestMappings([]);
                      setExecutionLogs([]);
                      setTestResults(null);
                      setActiveResultTab('walkthrough');
                    }}
                    className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Run New Test
                  </button>
                </>
              )}
              <button
                onClick={() => navigate('/sox-controls')}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 inline-flex items-center gap-2"
              >
                <CheckCircle2 className="h-4 w-4" />
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add File Modal */}
      {showAddFileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Add File to Step {(currentMappingStep || 0) + 1}</h3>
              <button
                onClick={() => {
                  setShowAddFileModal(false);
                  setCurrentMappingStep(null);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {(periodEvidenceFiles.length > 0 ? periodEvidenceFiles : uploadedFiles).filter(f => selectedFiles.includes(f.id)).map((file) => (
                <div
                  key={file.id}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => addFileToMapping(currentMappingStep, file)}
                >
                  <div className="flex items-center gap-3">
                    {getFileIcon(file.type)}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">{file.size}</p>
                    </div>
                    <Plus className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upload New Files Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Upload Evidence Files</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFiles([]);
                  setUploadEvidenceType('pbc');
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* SCRUM-402 Phase 3: evidence_type picker. Determines which
                  role the file lands in during analyze-stream:
                  pbc -> evidence, population_report -> role=population,
                  verification -> role=verification (C&A agents). */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload as</label>
                <select
                  value={uploadEvidenceType}
                  onChange={(e) => setUploadEvidenceType(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                >
                  <option value="pbc">Evidence (PBC) — mapped to test steps</option>
                  <option value="population_report">Population report — counted for sampling</option>
                  <option value="verification">Verification (GL, raw export, source docs) — C&amp;A checks</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  {uploadEvidenceType === 'population_report'
                    ? 'File is counted deterministically; drives the sample-size recommendation.'
                    : uploadEvidenceType === 'verification'
                      ? 'File is consumed by the Completeness & Accuracy agents. Not mapped to a step.'
                      : 'File is mapped to one or more test steps by the AI.'}
                </p>
              </div>

              {/* Drop zone */}
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-upload-input').click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-400', 'bg-blue-50'); }}
                onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50');
                  const files = Array.from(e.dataTransfer.files);
                  setUploadFiles(prev => [...prev, ...files]);
                }}
              >
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">Drag and drop files here</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); document.getElementById('file-upload-input').click(); }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Choose Files
                </button>
                <p className="text-xs text-gray-400 mt-3">Supports Excel, PDF, CSV, and image files</p>
                <input
                  id="file-upload-input"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setUploadFiles(prev => [...prev, ...files]);
                  }}
                />
              </div>

              {/* Selected files list */}
              {uploadFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Selected Files ({uploadFiles.length})</p>
                  {uploadFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        onClick={() => setUploadFiles(prev => prev.filter((_, i) => i !== idx))}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFiles([]);
                  setUploadEvidenceType('pbc');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadFiles}
                disabled={isLocked || uploadFiles.length === 0 || isUploading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md inline-flex items-center gap-2 ${uploadFiles.length === 0 || isUploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Files
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Testing Period Modal removed — period selection moved to Step 1 (SCRUM-267) */}

      {/* Additional Context Modal */}
      {showContextModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 text-lg">Re-run Test with Additional Context</h3>
              <p className="text-sm text-gray-500 mt-1">
                Provide additional context to improve test execution accuracy
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Guidance Card */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">What to include:</p>
                    <ul className="text-xs text-blue-700 mt-2 space-y-1 list-disc list-inside">
                      <li>Specific data points or ranges to focus on</li>
                      <li>Known issues or exceptions to consider</li>
                      <li>Additional validation rules or criteria</li>
                      <li>Business context or special circumstances</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Context Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Context
                </label>
                <textarea
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="E.g., Focus on transactions over $10,000. There was a known system outage on Jan 10th..."
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {additionalContext?.length || 0} characters
                </p>
              </div>

              {/* Confirmation when context entered */}
              {additionalContext && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <p className="text-sm text-green-700">Context will be applied to the test execution</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowContextModal(false);
                    setAdditionalContext('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRerunWithContext}
                  disabled={isLocked || !additionalContext?.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  <Play className="h-4 w-4" />
                  Re-run Test
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume or Start Fresh Modal - Only shows for INCOMPLETE tests */}
      {showResumeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 text-lg">
                {resumeMode === 'execution' ? 'Incomplete Test Run Found' : 'Previous Analysis Found'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                {resumeMode === 'execution'
                  ? `Test Run #${latestExecution?.test_run_number || 1} was interrupted. Would you like to resume or start a new test?`
                  : 'You have a saved evidence mapping from a previous analysis. Would you like to continue where you left off or start fresh?'
                }
              </p>

              {/* Show checkpoint info if available */}
              {resumeMode === 'execution' && localCheckpoint && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Info className="h-4 w-4" />
                    <span className="text-sm font-medium">Checkpoint saved</span>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    Last active: Step {localCheckpoint.checkpointStep} ({localCheckpoint.checkpointStepName})
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {/* Resume from checkpoint option (only for execution with checkpoint) */}
                {resumeMode === 'execution' && localCheckpoint && (
                  <button
                    onClick={() => {
                      // Mark as dismissed so modal doesn't re-appear
                      setCheckpointDismissed(true);
                      // Clear old checkpoint and restart the test execution
                      localStorage.removeItem(`testExecution_${id}`);
                      setExecutionLogs([]);
                      setTestResults(null);
                      setCurrentStep(3);
                      setShowResumeModal(false);
                      // Actually restart the test by calling startExecution after state updates
                      setTimeout(() => {
                        console.log('Restarting test execution from checkpoint');
                        startExecution();
                      }, 100);
                    }}
                    className="w-full p-4 border-2 border-amber-200 bg-amber-50 rounded-lg hover:border-amber-400 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Play className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Restart test execution</p>
                        <p className="text-sm text-gray-500">
                          Previous test was interrupted - restart from beginning
                        </p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Resume mapping option (only for mapping checkpoint) */}
                {resumeMode === 'mapping' && (
                  <button
                    onClick={resumeWithExistingMapping}
                    className="w-full p-4 border-2 border-purple-200 bg-purple-50 rounded-lg hover:border-purple-400 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Play className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Continue with saved mapping</p>
                        <p className="text-sm text-gray-500">Resume from Step 2: Map to Steps</p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Edit mapping option (for execution when mapping exists) */}
                {resumeMode === 'execution' && hasExistingMapping && (
                  <button
                    onClick={() => {
                      // Clear localStorage checkpoint and go to mapping step to edit
                      localStorage.removeItem(`test-execution-${id}`);
                      setLocalCheckpoint(null);
                      resumeWithExistingMapping(); // This loads mapping and goes to Step 2
                    }}
                    className="w-full p-4 border-2 border-purple-200 bg-purple-50 rounded-lg hover:border-purple-400 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Layers className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Edit evidence mapping</p>
                        <p className="text-sm text-gray-500">Review/modify mapping at Step 2, then run</p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Start fresh option */}
                <button
                  onClick={() => {
                    // Clear localStorage checkpoint before starting fresh
                    localStorage.removeItem(`test-execution-${id}`);
                    setLocalCheckpoint(null);
                    startFresh();
                  }}
                  className="w-full p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <RotateCcw className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Start fresh</p>
                      <p className="text-sm text-gray-500">Clear everything and select new files</p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="pt-2">
                <button
                  onClick={() => {
                    setCheckpointDismissed(true);
                    setShowResumeModal(false);
                    navigateBackFromRunTest();
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  Cancel and go back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render step result modal */}
      {selectedStepModal && (
        <StepResultModal
          step={selectedStepModal}
          onClose={() => setSelectedStepModal(null)}
        />
      )}

      {/* NEW: Edit Notes Modal */}
      {showEditNotesModal && selectedFileForNotes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Add Notes for {selectedFileForNotes.fileName}</h3>
                <p className="text-sm text-gray-500 mt-1">Add context about how this file is used for this test step</p>
              </div>
              <button
                onClick={() => {
                  setShowEditNotesModal(false);
                  setSelectedFileForNotes(null);
                  setUserNotes('');
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Error message */}
              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{saveError}</p>
                  </div>
                  <button
                    onClick={() => setSaveError(null)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* AI-generated summary (read-only) */}
              {selectedFileForNotes.usageSummary && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-purple-700">AI Summary:</p>
                  <p className="text-sm text-purple-900 mt-1">{selectedFileForNotes.usageSummary}</p>
                </div>
              )}

              {/* User notes textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Notes</label>
                <textarea
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any additional context or notes..."
                  value={userNotes}
                  onChange={(e) => setUserNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditNotesModal(false);
                  setSelectedFileForNotes(null);
                  setUserNotes('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveUserNotes}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Evidence Viewer Modal */}
      <EvidenceViewer
        citation={activeCitation}
        isOpen={evidenceViewerOpen}
        onClose={() => {
          setEvidenceViewerOpen(false);
          setActiveCitation(null);
          setActiveStepFindings(null);
        }}
        stepFindings={activeStepFindings}
        executionId={activeExecutionId}
      />
      <ModuleAccessDeniedDialog {...testExecutionsModuleAccessDialogProps} />
    </div>
  );
}

export default RunTestFlow;
